/*
 * CareIQ N3 (BUILD-PLAN Step 4, commit 2; the approved mock is
 * careiq-final.html, the data contract is careiq.n3.1, and n3.2 for the
 * source links).
 *
 * Mounted instead of CareIQTab.jsx for a login with the N3 flag (9928 only;
 * patientchartwrap.jsx). CareIQTab.jsx itself is untouched.
 *
 *   Date of Service row first, then
 *   before recording   AI Recommendations drawer (8 merged items; owner 10-07,
 *                      V4 "order ledger": each row's order is its Actions cell,
 *                      no filled button, no review control; careiq.n3.4, the
 *                      n3.3 fallback) + Follow up heading the Patient Timeline
 *                      (a stacked tab, narrower than 880 px: the critical
 *                      Follow up row leads ABOVE the drawer)
 *   Every part lays out by its OWN width, never the screen's (responsive
 *   audit 10-07): the tab (N3/useStackedLayout.js), and the drawer, Follow up,
 *   the timeline, the order zone and the DOS row (CSS container queries,
 *   CareIQTabN3.css).
 *   recording / after  the drawer collapses to a sticky one-item band and the
 *                      order zone appears (Current card, then the Suggested
 *                      card with the first LiveScribe suggestion)
 *
 * Data: the v2 GET /v1/kg/{p}/careiq/{pid} (merged list, Follow up, document
 * labels; no Athena call), the v2 lab list, documents, orders, encounters and
 * the BlinkScribe rows. If the GET fails, the old v1 kg/todo shape with
 * client grouping and no review state. The lab sync stays lazy: a timeline
 * search of 2+ characters or "Show all", never the chart open (a sync is a
 * paid Athena call). Order Undo deletes only an order this tab created, with
 * the patient-scoped DELETE orders/delete/multiple and the chart's order date.
 *
 * Source links (careiq.n3.2; an n3.1 body renders as before): each open
 * recommendation lists its evidence (the patient's lab result dates and
 * documents) and its vetted guideline. A lab opens the lab popup of that
 * result date (LabsMiniModal, the one the timeline opens; matched by date and
 * test name, the lab list has no lab id), a document opens the document
 * viewer through openReport (the path a Follow up document row opens by).
 * Follow up rows are clicked like timeline rows (the whole row opens its
 * document or its lab popup; no "Open report" text), one row per multi-take
 * upload, and the timeline head holds the critical row + 2 more ("N more"),
 * so the patient's own timeline stays in view. The timeline never repeats
 * takes (N3/docGroups): one row "Electrocardiogram ×3" opening the newest,
 * hidden while Follow up offers the group. Nothing here calls Athena.
 *
 * A practice without CareIQ orders (config/practice.js careiqOrdersShown(),
 * 7825): no Add / Undo, no order zone, no "Show orders without recording", no
 * diagnosis confirm; an order reads as text. A next step or "Open Full Labs"
 * whose chart tab the practice does not have reads as text / is left out. The
 * lazy lab sync is off where careiqLabSync() says so (7825).
 *
 * No visit today (owner 10-09): unless the chart is on the patient's live
 * visit today (visitGuard.js isLiveTodayVisit: today, not cancelled /
 * rescheduled / no-show), the tab opens on "No visit today" (N3/NoVisitToday):
 * the DOS row names no date, no recommendations, no Follow up, no order zone;
 * the timeline stays. A visit picked on the timeline opens its note (the visit
 * popup) and the tab shows that visit -- its date in the DOS row, a "Viewing
 * visit of ..." banner with "Back to today", its CareIQ view only (no orders,
 * no review writes: N3/ordersShown.js, stateOk). The picked visit is the
 * chart's recording target (utils/recordingTarget.js, shared with the
 * sidebar mic): the mic records on it (after a confirmation), and while a
 * recording runs the pick is locked (a new pick is refused with a notice).
 *
 * TDZ rule: every derived const is declared above its first use (render-time
 * ReferenceErrors blank the chart and smoke.js cannot catch them).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import api from "../../../../api/api";
import careiqapi from "../../../../api/careiqapi";
import blinkscribeapi from "../../../../api/blinkscribeapi";
import { useRecording } from "../../../../context/RecordingContext";
import { eventBus } from "../../../../utils/eventBus";
import { setConfig } from "../../../../reduxtoolkit/userconfigslice";
import { careiqLabSync, careiqOrdersShown, chartTabAllowed, getPracticeId } from "../../../../config/practice";
import { isPreviewBody, previewLine, withPreviewParams } from "../../../../utils/careiqPreview";
import { classifyVisitRows, normalizeApptId, rowAppointmentId, sameAppointment } from "../../../../utils/recordingCompletion";
import { classifyVisit, isLiveTodayVisit, visitNotHeld, visitStatusWord, visitTimeLabel } from "../../../../utils/visitGuard";
import { clinicTodayKey } from "../../../../landingscreen/cards/visitflow";
import { imagingIdSet, isImagingReportTwin } from "../Document/documentTwins";
import ProcedureDocument from "./ProcedureDocument";
import { applyListAnswer, documentsAsOfLine, withRetry } from "./careiqFetch";
import DosRow from "./N3/DosRow";
import RecsDrawer from "./N3/RecsDrawer";
import RecsOverlay from "./N3/RecsOverlay";
import { criticalLabs, laneOrder } from "./N3/recLanes";
import { NEXT_TAB, sharersOf } from "./N3/orderLedger";
import { schemaRetry } from "./N3/recsModel";
import { reasonText } from "./N3/recText";
import { orderDxDiagnosis } from "./N3/OrderDx";
import FollowUp from "./N3/FollowUp";
import PatientTimeline from "./N3/PatientTimeline";
import OrderZone from "./N3/OrderZone";
import { isV2Announce, isV2ForVisit, newerV2, v2Discussed, v2Other, v2Placed, v2Rows, v2StoreKey } from "./N3/orderV2";
import { changeVisitOrderWhen, orderCreateBody, placedWhenEditable } from "./N3/orderCreate";
import { liveStateFor } from "../../../../components/OrderReview/liveOrdersModel";
import { openOrderReview, registerOrderReviewVisit } from "../../../../components/OrderReview/OrderReviewHost";
import { emptyRecord, readRecord, reviewEnabled, reviewStoreKey, visitKeyOf, writeRecord } from "../../../../components/OrderReview/orderReviewModel";
import { DetailModal, LabsMiniModal, VisitSummaryModal, isOutOfRange } from "./N3/legacyModals";
import { useVisitPhase } from "./N3/useVisitPhase";
import { STACKED_FALLBACK_Q, useStackedLayout } from "./N3/useStackedLayout";
import { OrdersShownContext } from "./N3/ordersShown";
import NoVisitToday from "./N3/NoVisitToday";
import { visitFromTimeline } from "./N3/noVisit";
import {
  chartKeyOf,
  clearRecordingVisit,
  dismissRecordingNotice,
  recordingTargetFor,
  selectRecordingVisit,
  useRecordingTargetState,
} from "../../../../utils/recordingTarget";
import { FALLBACK_ORDER_ALIASES, isAddedOrder, mapOrderType, orderKey } from "./N3/orderKey";
import {
  SCHEMA, addedText, applyStateUpdates, buildChange, clearGoneOrders, clockTime, existingOrderKeys, hiddenEarlierVisit,
  knownSchema, mergedFromLine, openRecs, optimisticUpdated, recs, recsFromLegacyTodo, reviewedThisVisit, setExistingOrder,
  stateAvailable,
} from "./N3/recsModel";
import { labGroupFor, labGroupFromRecord, labsByDay } from "./N3/sourceLinks";
import { docCategory, docIdOf, groupLabel, groupTakes, serverGroups } from "./N3/docGroups";
import { fuMode as computeFuMode, fuRestEmpty, fuRows, fuSplit as computeFuSplit, newestReviewed, openFuRows, reviewedFuThisVisit } from "./N3/followUpModel";
import "./CareIQTabN3.css";

const DESKTOP_Q = "(min-width:1024px)";
const DEFAULT_TIMELINE_DOCS = ["imagingresult", "clinicaldocument"];
const ICD10_PATTERN = /^[A-Z]\d{2}(\.\d{1,4})?$/i;

function useMedia(query) {
  const read = () => {
    try {
      return Boolean(window.matchMedia && window.matchMedia(query).matches);
    } catch (_) {
      return false;
    }
  };
  const [match, setMatch] = useState(read);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener("change", on);
    else if (mq.addListener) mq.addListener(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", on);
      else if (mq.removeListener) mq.removeListener(on);
    };
  }, [query]);
  return match;
}

// Below 1024 px the chart page scrolls under the chart window's close button
// (top right): anything N3 scrolls into view lands below it.
const TOP_CLEAR = 56;
// the end-of-visit order review opens with the last live payload when the final pass has not landed by then
const REVIEW_FALLBACK_MS = 25000;

const reducedMotion = () => {
  try {
    return !window.matchMedia || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (_) {
    return true;
  }
};

const readJson = (key, fallback) => {
  if (!key) return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (_) {
    return fallback;
  }
};
const writeJson = (key, value) => {
  if (!key) return;
  try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* full / disabled: non-fatal */ }
};

const NO_DOCS = { imagingresult: [], clinicaldocument: [] };
// The timeline's reads opt in to an old cached list (served at once, rebuilt in the background,
// re-read at +15 s / +45 s): the document lists, the encounters and the progress notes. Sent as a
// query parameter (axios params), so the URLs stay the ones other screens call without it.
const STALE_OK = { params: { allow_stale: 1 } };
const HANDWRITTEN_EXCLUDE = ["ekg", "ecg", "electrocardiogram", "imaging", "x-ray", "xray", "mri", "ct scan", "ultrasound", "lab"];
const handwrittenOnly = (docs) => (Array.isArray(docs) ? docs : []).filter((d) => {
  const desc = ((d && (d.documentdescription || d.description)) || "").toLowerCase();
  return !HANDWRITTEN_EXCLUDE.some((p) => desc.includes(p));
});

const rowsOf = (res) => {
  const d = res && res.data !== undefined ? res.data : res;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  return [];
};

// Nearest ancestor that scrolls (the CareIQ root at >= 1024 px, the chart page below).
const scrollerOf = (el) => {
  let n = el && el.parentElement;
  while (n && n !== document.body) {
    const cs = getComputedStyle(n);
    if (/(auto|scroll)/.test(cs.overflowY) && n.scrollHeight > n.clientHeight + 1) return n;
    n = n.parentElement;
  }
  return document.scrollingElement || document.documentElement;
};

export default function CareIQTabN3({ dataId, onSwitchTab }) {
  /* ───────── identity ───────── */
  const practiceId = getPracticeId();
  const patientId = dataId?.patientid;
  // Owner 10-09: the tab works on a visit only when the chart is on the patient's visit TODAY that is
  // happening (not cancelled, rescheduled or no-show; utils/visitGuard.js isLiveTodayVisit). Any other
  // chart card (a past or future visit, a cancelled / no-show row, no appointment: the chart resolver
  // keeps such a card only when there is no live visit today) opens on "No visit today": no date of
  // service, no recommendations, no Follow up; the timeline stays. A visit picked on the timeline is
  // then shown (its date, its note, its CareIQ) view only, until "Back to today".
  const chartLive = isLiveTodayVisit(dataId);
  // The picked visit IS the chart's recording target (owner 10-09, utils/recordingTarget.js): one store
  // shared with the sidebar's mic and RecordingContext. A pick belongs to this chart card only (its
  // key); while a recording of this patient runs its visit is locked -- a pick is refused with a notice
  // and the tab keeps showing the recording's visit.
  const targetState = useRecordingTargetState();
  const cardKey = chartKeyOf(dataId);
  const selection = targetState.selection && targetState.selection.chartKey === cardKey ? targetState.selection : null;
  const lockHere = targetState.lock && String(targetState.lock.patientid ?? "") === String(patientId ?? "") ? targetState.lock : null;
  const recTarget = recordingTargetFor(dataId, targetState);
  const picked = selection ? selection.pick : !chartLive && lockHere ? lockHere.row : null;
  const setPicked = useCallback((v) => {
    if (v) selectRecordingVisit(dataId, v);
    else clearRecordingVisit();
  }, [dataId]);
  const visit = chartLive ? dataId : picked;
  const noVisit = !visit;
  // orders, diagnosis confirms and review state are written only for today's live visit
  const interactive = chartLive;
  // the practice lets this tab place orders and confirm diagnoses (9928; not 7825), on today's live visit
  const ordersOn = careiqOrdersShown() && interactive;
  const appointmentId = visit ? visit.appointmentid : null;
  const chartOrderDate = useMemo(() => {
    const rawDate = visit ? visit.startdate || visit.encounterdate || visit.date : null;
    const parsed = rawDate ? dayjs(rawDate) : null;
    return parsed && parsed.isValid() ? parsed.format("YYYY-MM-DD") : clinicTodayKey();
  }, [visit]);
  const chartProviderName = visit ? visit.linked_provider || visit.providername || "" : "";
  const chartEncounterId = Number(visit?.encounterid) || 0;
  // "Cancelled" / "No show" / "Rescheduled" on a picked visit that did not happen
  const visitStatus = visit && !chartLive ? visitStatusWord(visit) : "";
  const userConfig = useSelector((s) => s.userconfig);
  const me = userConfig?.username || userConfig?.config?.username || "";
  const dispatch = useDispatch();
  // the chart WINDOW's scroll model (>= 1024 px the CareIQ root scrolls): the screen's, not a layout's
  const desktop = useMedia(DESKTOP_Q);

  /* ───────── data state ───────── */
  const [body, setBody] = useState(null);
  const [source, setSource] = useState("loading"); // loading | n3 | fallback | denied | mismatch
  const [notRun, setNotRun] = useState(false);
  // Both the GET and the old kg/todo failed (not a 404): nothing was loaded,
  // which must never read as "Nothing open".
  const [loadFailed, setLoadFailed] = useState(false);
  const bodyRef = useRef(null);
  bodyRef.current = body;
  const [labPanels, setLabPanels] = useState([]);
  // imaging + clinical document lists, kept per category: each arrives (and is re-read) on its own
  const [docsByCat, setDocsByCat] = useState(NO_DOCS);
  const documents = useMemo(() => [...docsByCat.imagingresult, ...docsByCat.clinicaldocument], [docsByCat]);
  const [docsLoaded, setDocsLoaded] = useState(false);
  // {list: {asOf, refreshing, polling}} for lists the server answered from an old cache and is rebuilding
  const [listMetas, setListMetas] = useState({});
  const [encDocuments, setEncDocuments] = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [bsRows, setBsRows] = useState([]);
  const [handwrittenDocs, setHandwrittenDocs] = useState([]);
  const [currentOrders, setCurrentOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [allLabsLoaded, setAllLabsLoaded] = useState(false);
  const [allLabsLoading, setAllLabsLoading] = useState(false);
  const [practicePrefs, setPracticePrefs] = useState(null);
  const [apptReason, setApptReason] = useState("");
  const reduxOrdersData = useSelector((s) => s.ordersData?.ordersdata);
  const reduxDiagnoses = useSelector((s) => s.orders?.orders?.diagnosis || []);
  const reduxLov = useSelector((s) => s.dump?.storedvalues?.find?.((i) => i.id === "lov")?.value);
  const reduxTov = useSelector((s) => s.dump?.storedvalues?.find?.((i) => i.id === "tov")?.value);

  /* ───────── UI state ───────── */
  const [revOpen, setRevOpen] = useState(false);
  const [fuRevOpen, setFuRevOpen] = useState(false);
  const [fuShow, setFuShow] = useState(false);
  const [fuOpen, setFuOpen] = useState(false);
  // the timeline head's "N more · Show" (Follow up beyond the critical row + 2)
  const [fuMore, setFuMore] = useState(false);
  const [foldedOpen, setFoldedOpen] = useState(false);
  const [ovlOpen, setOvlOpen] = useState(false);
  const [ovlAnim, setOvlAnim] = useState(false);
  const [ovlMax, setOvlMax] = useState(null);
  const [viewSnap, setViewSnap] = useState(null);
  const [flashKey, setFlashKey] = useState(null);
  const [justDone, setJustDone] = useState(null);
  const [timelineSearch, setTimelineSearch] = useState("");
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const [tlPrefsOpen, setTlPrefsOpen] = useState(false);
  const [tlPrefsSaving, setTlPrefsSaving] = useState(false);
  const [tlDraft, setTlDraft] = useState(DEFAULT_TIMELINE_DOCS);
  const [modalItem, setModalItem] = useState(null);
  const [visitModal, setVisitModal] = useState(null);
  const [labModal, setLabModal] = useState(null);
  const [docModal, setDocModal] = useState(null);
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingSaving, setEditingSaving] = useState(false);
  const [confirmDeleteOrder, setConfirmDeleteOrder] = useState(null);
  const [editError, setEditError] = useState("");
  const [sessionAdds, setSessionAdds] = useState(() => new Map());
  const [busyKeys, setBusyKeys] = useState(() => new Set());
  const [errors, setErrors] = useState(() => new Map());
  const [freshOrderId, setFreshOrderId] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [noteLov, setNoteLov] = useState(0);
  const [aiDiagnoses, setAiDiagnoses] = useState([]);
  const [confirmedDx, setConfirmedDx] = useState(() => new Set());
  const [persistedLive, setPersistedLive] = useState([]);
  // the diagnosis the provider chose for a rec's order (careiq.n3.4 action.orderDx, N3/OrderDx.jsx), by
  // issue_key; none chosen: the best (first) code
  const [dxPick, setDxPick] = useState(() => new Map());
  const recStackRef = useRef([]);
  const fuStackRef = useRef([]);
  const refetchedForListRef = useRef(false);
  // Orders-list fetch counter: a fetch that STARTED after an add (or after
  // the GET that reported an existing order) and lacks that order id means
  // the order was deleted (here or in the Orders panel).
  const ordersSeqRef = useRef(0);
  const bodySeqRef = useRef(0);
  const rootRef = useRef(null);
  const wrapRef = useRef(null);
  const bandWrapRef = useRef(null);
  const sinceMainRef = useRef(null);
  const sidebarRef = useRef(null);
  const tlScrollRef = useRef(null);
  const escapeRef = useRef(null);
  const seenLiveRef = useRef(new Set());
  const zoneSeenRef = useRef(false);
  const [rootH, setRootH] = useState(null);
  // side by side or stacked by the TAB's own width (N3/useStackedLayout.js), never the screen's
  const stacked = useStackedLayout(rootRef, STACKED_FALLBACK_Q);

  /* ───────── recording ───────── */
  const {
    suggestedOrders: rawLiveScribeOrders,
    suggestedICD10: rawLiveScribeDx,
    orderSuggestionsV2: rawOrderV2,
    liveOrders: rawLiveOrdersCard,
    patientInfo: recordingPatientInfo,
    lastNotePatientId,
    lastCompletedRecording,
  } = useRecording();

  // A recording on a chart with no visit today runs on the SELECTED visit (utils/recordingTarget.js): the
  // tab shows that visit (`picked` falls back to the locked recording's visit), its date unmistakable,
  // never "No visit today" under a live recording.

  // This visit's own recording rows (no extra fetch: the timeline's
  // recording/details rows, filtered to the appointment).
  const visitRows = useMemo(() => {
    const appt = normalizeApptId(appointmentId);
    const mine = bsRows.filter((r) => {
      if (!r) return false;
      if (appt) return rowAppointmentId(r) === appt;
      const d = dayjs(r.ts || r.created_at || r.date);
      return d.isValid() && d.format("YYYY-MM-DD") === chartOrderDate;
    });
    return classifyVisitRows(mine);
  }, [bsRows, appointmentId, chartOrderDate]);

  const vp = useVisitPhase({ patientId, appointmentId, visitRows });
  const phase = vp.phase;
  const before = phase === "before";

  // The context's live LiveScribe suggestions are the NEWEST recording's,
  // whichever visit it was for, and they outlive it (until the next chart
  // open clears them; the sidebar does that after this tab's first render).
  // They are THIS visit's only while it records here, after a recording made
  // here in this chart window, or when the context's finished recording is
  // this appointment's -- never a sibling or follow-up appointment's of the
  // same patient (they would be shown, persisted under this visit and could
  // be ordered on this visit's date), and never a discarded recording's.
  const apptKey = normalizeApptId(appointmentId);
  const [liveOwn, setLiveOwn] = useState({ owned: false, blocked: false });
  const liveOwnTrackRef = useRef({ phase, visit: `${patientId}:${apptKey}` });
  useEffect(() => {
    const t = liveOwnTrackRef.current;
    const visit = `${patientId}:${apptKey}`;
    liveOwnTrackRef.current = { phase, visit };
    if (t.visit !== visit) setLiveOwn({ owned: phase === "recording", blocked: false });
    else if (phase === "recording") setLiveOwn((s) => (s.owned && !s.blocked ? s : { owned: true, blocked: false }));
    else if (t.phase === "recording" && phase === "before") setLiveOwn({ owned: false, blocked: true });
  }, [phase, patientId, apptKey]);
  const livePatientHere =
    !!patientId &&
    (String(recordingPatientInfo?.patientid || "") === String(patientId) || String(lastNotePatientId || "") === String(patientId));
  const completedHere = Boolean(lastCompletedRecording) && (apptKey
    ? sameAppointment(lastCompletedRecording.appointmentId, apptKey)
    : String(lastCompletedRecording.patientId || "") === String(patientId));
  const liveStateBelongsHere = livePatientHere && !liveOwn.blocked && (phase === "recording" || liveOwn.owned || completedHere);
  const liveBelongsRef = useRef(liveStateBelongsHere);
  liveBelongsRef.current = liveStateBelongsHere;
  const liveScribeOrders = useMemo(
    () => (liveStateBelongsHere && Array.isArray(rawLiveScribeOrders) ? rawLiveScribeOrders : []),
    [liveStateBelongsHere, rawLiveScribeOrders],
  );
  const liveScribeDx = useMemo(
    () => (liveStateBelongsHere && Array.isArray(rawLiveScribeDx) ? rawLiveScribeDx : []),
    [liveStateBelongsHere, rawLiveScribeDx],
  );

  /* ───────── order suggestions v2 (BlinkScribe order_suggest_v2; enabled providers only, N3/orderV2.js) ───────── */
  // Kept per visit ({ enabled, payload }) so a reload keeps it; filled from the recording socket (the
  // payload names its own appointment and patient) and, after the stop, from BlinkScribe's GET. A visit
  // without a v2 announce never stores anything, never fetches, and shows today's LiveScribe list.
  const v2Key = v2StoreKey(patientId, apptKey);
  // the end-of-visit order review record of this visit (components/OrderReview, enabled providers only)
  const reviewKey = reviewStoreKey(patientId, apptKey);
  const [v2Stored, setV2Stored] = useState(null);
  const [v2Picks, setV2Picks] = useState(() => new Map());
  const [v2Checks, setV2Checks] = useState(() => new Map());
  useEffect(() => {
    const st = readJson(v2Key, null);
    setV2Stored(st && typeof st === "object" && st.enabled ? st : null);
    setV2Picks(new Map());
    setV2Checks(new Map());
  }, [v2Key]);
  const v2Incoming = !liveOwn.blocked && isV2Announce(rawOrderV2, patientId, apptKey) ? rawOrderV2 : null;
  useEffect(() => {
    if (!v2Incoming || !v2Key) return;
    setV2Stored((prev) => {
      const payload = isV2ForVisit(v2Incoming, patientId, apptKey) ? newerV2(prev && prev.payload, v2Incoming) : (prev && prev.payload) || null;
      const next = { enabled: true, payload };
      writeJson(v2Key, next);
      return next;
    });
  }, [v2Incoming, v2Key, patientId, apptKey]);
  const v2Enabled = Boolean(v2Stored && v2Stored.enabled);
  const v2Final = Boolean(v2Stored && v2Stored.payload && v2Stored.payload.final);
  // After the stop: the final pass lands in BlinkScribe a few seconds later (the socket is closed by then).
  useEffect(() => {
    if (!v2Enabled || v2Final || !v2Key || phase === "recording" || phase === "before") return undefined;
    let cancelled = false;
    let timer = null;
    let i = 0;
    const delays = [1500, 4000, 8000, 15000, 30000, 60000];
    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await blinkscribeapi.get("/v1/recording/order-suggestions/visit", {
          params: { appointmentid: Number(apptKey), patientid: Number(patientId) }, timeout: 15000,
        });
        const p = res && res.payload;
        if (cancelled) return;
        if (isV2ForVisit(p, patientId, apptKey)) {
          setV2Stored((prev) => {
            const next = { enabled: true, payload: newerV2(prev && prev.payload, p) };
            writeJson(v2Key, next);
            return next;
          });
          if (p.final) return;
        }
        if (res && res.enabled === false) return;
      } catch (_) {
        // keep what is shown (the live payload, or today's LiveScribe list)
      }
      if (!cancelled && i < delays.length) timer = setTimeout(tick, delays[i++]);
    };
    timer = setTimeout(tick, delays[i++]);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [v2Enabled, v2Final, v2Key, phase, apptKey, patientId]);
  const v2Payload = v2Stored && isV2ForVisit(v2Stored.payload, patientId, apptKey) ? v2Stored.payload : null;
  // The live one-tap Orders card (owner 10-23; components/OrderReview/LiveOrdersCard in the LiveScribe panel) is on
  // for this visit: it replaces the Suggested orders list here, and LiveOrdersHost opens the end-of-visit review
  // (whichever tab is open). Everyone else: unchanged.
  const liveCardOn = Boolean(liveStateFor(rawLiveOrdersCard, patientId, apptKey)
    || (v2Payload && v2Payload.live_orders && v2Payload.live_orders.enabled === true));

  /* ───────── derived: the CareIQ body ───────── */
  const meta = (body && body.meta) || null;
  const aliases = (meta && meta.order_aliases) || FALLBACK_ORDER_ALIASES;
  // (never for a view that is not today's live visit: nothing is reviewed on a picked visit)
  const stateOk = source === "n3" && stateAvailable(body) && interactive;
  const dosYear = String((meta && meta.visit && meta.visit.dos) || chartOrderDate).slice(0, 4);
  const allRecs = recs(body);
  const recsByKey = useMemo(() => new Map(allRecs.map((r) => [r.issue_key, r])), [allRecs]);
  // every rec in lane order (a shared order's pointer finds and names the row that adds it)
  const allOrdered = useMemo(() => laneOrder(allRecs), [allRecs]);
  const openList = openRecs(body);
  const hiddenRecs = hiddenEarlierVisit(body);
  const totalShown = allRecs.length - hiddenRecs.length;
  const fuOpenRows = openFuRows(body);
  const existingKeys = useMemo(() => existingOrderKeys(body), [body]);
  const merged = meta ? mergedFromLine(meta) : "";
  const lastVisitDate = body && body.followup && body.followup.last_visit ? body.followup.last_visit.date : null;
  const visitApptId = (meta && meta.visit && meta.visit.appointmentid) || (normalizeApptId(appointmentId) ? Number(appointmentId) : null);

  /* ───────── orders ───────── */
  const filteredOrders = useMemo(
    () => currentOrders.filter((o) => {
      const name = (o.ordername || o.name || "").trim();
      if (ICD10_PATTERN.test(name)) return false;
      if ((o.ordertype || "").toLowerCase() === "diagnosis") return false;
      return true;
    }),
    [currentOrders],
  );
  const sessionKeys = useMemo(() => [...sessionAdds.keys()], [sessionAdds]);
  const addedKeys = useMemo(() => [...sessionKeys, ...existingKeys], [sessionKeys, existingKeys]);
  const isAdded = useCallback(
    (nameOrKey) => isAddedOrder(nameOrKey, { aliases, currentOrders: filteredOrders, extraKeys: addedKeys }),
    [aliases, filteredOrders, addedKeys],
  );

  // An item's action: its button, or the confirmation in place.
  const infoFor = useCallback((item) => {
    const a = item && item.action;
    if (!a) return null;
    const key = a.order_key || orderKey(a.ordername, aliases);
    if (busyKeys.has(key)) return { state: "add", busy: true };
    // short: the Actions cell's word ("✓ Added" by this tab, "✓ Ordered" already on the visit)
    const s = sessionAdds.get(key);
    if (s) return { state: "added", text: addedText({ from: s.from, pre: s.pre, addedHere: true }), short: "Added", canUndo: true };
    const ex = a.existing_order;
    if (ex) return { state: "added", text: addedText({ from: "rec", pre: true, addedHere: Boolean(ex.added_here) }), short: ex.added_here ? "Added" : "Ordered", canUndo: Boolean(ex.added_here) && stateOk };
    if (isAdded(key)) return { state: "added", text: addedText({ addedHere: false }), short: "Ordered", canUndo: false };
    return { state: "add", error: errors.get(key) || null };
  }, [aliases, busyKeys, sessionAdds, errors, isAdded, stateOk]);

  // The grey reason under a Current Orders row this tab (or CareIQ) added.
  const infoForOrder = useCallback((order) => {
    if (order._added) return { why: order._why || "" };
    for (const s of sessionAdds.values()) if (s.order_id && order.id === s.order_id) return { why: s.why || "" };
    const items = [...allRecs, ...fuRows(body)];
    for (const it of items) {
      const ex = it.action && it.action.existing_order;
      if (ex && ex.order_id === order.id) return { why: reasonText(it.action.why, it.headline) };
    }
    return null;
  }, [sessionAdds, allRecs, body]);

  /* ───────── live suggestions (LiveScribe only, D10) ───────── */
  const liveKeyPersist = patientId && normalizeApptId(appointmentId) ? `careiq:liveOrders:${patientId}:${normalizeApptId(appointmentId)}` : null;
  const persistKey = patientId && appointmentId ? `careiq:confirmedDx:${patientId}:${appointmentId}` : null;
  const aiDxKey = patientId && appointmentId ? `careiq:aiDiagnoses:${patientId}:${appointmentId}` : null;
  const showLive = !before || vp.escape;

  const liveOrders = useMemo(() => {
    const seen = new Set();
    const out = [];
    [...persistedLive, ...liveScribeOrders].forEach((entry) => {
      const raw = typeof entry === "string" ? entry : entry?.order_name || entry?.order || "";
      const name = String(raw).replace(/^Order\s+/i, "").trim();
      const key = orderKey(name, aliases);
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push({
        key, name,
        order_type: typeof entry === "string" ? null : entry?.order_type || entry?.ordertype || null,
        details: typeof entry === "string" ? "" : entry?.details || entry?.detail || "",
      });
    });
    return out;
  }, [persistedLive, liveScribeOrders, aliases]);

  const liveOrderRows = useMemo(() => liveOrders.map((o) => {
    const s = sessionAdds.get(o.key);
    const added = isAdded(o.key);
    return {
      ...o,
      added,
      busy: busyKeys.has(o.key),
      // greyed with a tooltip when it was already on the visit before it was suggested
      pre: added && !(s && s.from === "live"),
      isNew: zoneSeenRef.current && !seenLiveRef.current.has(o.key),
    };
  }), [liveOrders, sessionAdds, isAdded, busyKeys]);

  const v2RowList = useMemo(
    () => (v2Payload ? v2Rows(v2Payload, { aliases, picks: v2Picks, checks: v2Checks, isAdded, sessionAdds, busyKeys }) : []),
    [v2Payload, aliases, v2Picks, v2Checks, isAdded, sessionAdds, busyKeys],
  );
  const v2OtherRows = useMemo(
    () => (v2Payload ? v2Other(v2Payload, { aliases, isAdded, sessionAdds, busyKeys, skipKeys: v2RowList.map((r) => r.key) }) : []),
    [v2Payload, aliases, isAdded, sessionAdds, busyKeys, v2RowList],
  );

  const liveDx = useMemo(() => aiDiagnoses.map((dx) => {
    const key = dx.description || dx.icd10_code || "";
    return { ...dx, key, confirmed: confirmedDx.has(key) };
  }), [aiDiagnoses, confirmedDx]);

  const currentDx = useMemo(() => {
    const reduxKeys = new Set(reduxDiagnoses.map((c) => c.condition_name || c.icd10 || ""));
    const ordersDx = (reduxOrdersData || [])
      .filter((o) => (o.ordertype || "").toLowerCase() === "diagnosis")
      .map((o) => ({ condition_name: o.ordername || o.description || o.name || "", icd10: o.icd10 || o.code || "" }))
      .filter((d) => d.condition_name && !reduxKeys.has(d.condition_name));
    const localConfirmed = aiDiagnoses
      .filter((dx) => confirmedDx.has(dx.description || dx.icd10_code || ""))
      .filter((dx) => !reduxKeys.has(dx.description || ""))
      .map((dx) => ({ condition_name: dx.description, icd10: dx.icd10_code }));
    return [...reduxDiagnoses, ...ordersDx, ...localConfirmed].map((c, i) => ({ ...c, key: `${c.icd10 || ""}:${c.condition_name || ""}:${i}` }));
  }, [reduxDiagnoses, reduxOrdersData, aiDiagnoses, confirmedDx]);

  /* ───────── chart preferences (timeline docs, section flags) ───────── */
  const timelineDocs = useMemo(() => {
    const user = userConfig?.config?.timeline_docs;
    if (Array.isArray(user) && user.length > 0) return user;
    const practice = practicePrefs?.timeline?.docs;
    if (Array.isArray(practice) && practice.length > 0) return practice;
    return DEFAULT_TIMELINE_DOCS;
  }, [userConfig, practicePrefs]);
  const ciqSections = practicePrefs?.careiq?.sections || null;
  const sectionOn = useCallback((k) => !ciqSections || ciqSections[k] !== false, [ciqSections]);
  const showBand = sectionOn("care_gaps") || sectionOn("recommendations") || sectionOn("risk_factors");
  const showFollowUp = sectionOn("follow_ups") || sectionOn("critical_values");
  const showSuggested = sectionOn("suggested_diagnoses") || sectionOn("suggested_orders");
  const wantsEncDocs = timelineDocs.includes("encounterdocument");

  /* ───────── modes ───────── */
  const bandMode = before && !vp.escape ? "drawer" : openList.length ? "band" : "line";
  const fuModeValue = computeFuMode({ openCount: fuOpenRows.length, fuShow, fuOpen, before, stacked });
  const split = computeFuSplit({ stacked, before, mode: fuModeValue });
  // the rest of the split heads the timeline only when it holds something
  const restShown = split && !fuRestEmpty(body, stateOk);
  // The drawer / overlay list: items that were open when it opened stay in
  // place (a handled item confirms where it is), plus anything open now.
  const viewKeys = useMemo(() => {
    const snap = new Set(viewSnap || []);
    return allRecs.filter((r) => r.status === "open" || snap.has(r.issue_key)).map((r) => r.issue_key);
  }, [allRecs, viewSnap]);
  // (the chart's level / type of visit -- Redux, the note -- belong to today's live visit only)
  const lovValue = interactive ? Number(reduxLov) || noteLov || Number(dataId?.lov) || 0 : 0;
  const visitTov = interactive ? reduxTov || dataId?.tov : visit?.tov;
  const visitIsNewPatient = visitTov ? visitTov === "New Patient" : /\bnew\b/i.test(String(visit?.purpose || ""));
  const visitLovCode = lovValue >= 1 && lovValue <= 5 ? String(visitIsNewPatient ? 99200 + Math.max(2, lovValue) : 99210 + lovValue) : null;
  const visitKind = String(visitTov || visit?.purpose || visit?.appointmenttypename || "Visit").trim();
  const visitText = visit ? `${visitKind}${/visit/i.test(visitKind) ? "" : " visit"} ${visitTimeLabel(visit)}`.trim() : "";
  const dosText = chartOrderDate ? dayjs(chartOrderDate).format("MMMM D, YYYY") : "—";
  // the no-visit screen's lines about the chart card itself (classifyVisit: the clinic's calendar day)
  const cardInfo = useMemo(() => (chartLive ? null : classifyVisit(dataId)), [chartLive, dataId]);

  /* ───────── fetches ───────── */
  const loadCareiqBody = useCallback(async () => {
    // ?schema=careiq.n3.4 asks for the order ledger's fields (owner 10-07, V4), on top of n3.3's
    // shorthand headlines (CONTRACT §19) and n3.2's source links (§18); a server that does not know
    // the schema answers careiq.n3.1, so the tab asks again one step down (n3.3, then n3.2). The
    // specialty preview's parameter rides on every ask (the re-asks spread `params`).
    let params = { dos: chartOrderDate };
    if (normalizeApptId(appointmentId)) params.appointmentid = Number(normalizeApptId(appointmentId));
    params.schema = SCHEMA;
    params = withPreviewParams(params);      // specialty preview only (utils/careiqPreview.js)
    try {
      let b = await careiqapi.get(`/v1/kg/${practiceId}/careiq/${patientId}`, { params, timeout: 8000 });
      let asked = SCHEMA;
      for (let again = b && schemaRetry(b.schema, asked); again; again = b && schemaRetry(b.schema, asked)) {
        asked = again;
        // eslint-disable-next-line no-await-in-loop
        b = await careiqapi.get(`/v1/kg/${practiceId}/careiq/${patientId}`, { params: { ...params, schema: again }, timeout: 8000 });
      }
      if (!b || !knownSchema(b.schema) || !b.meta || !Array.isArray(b.recommendations) || !b.followup) throw new Error("unexpected CareIQ body");
      return { source: "n3", body: b };
    } catch (e) {
      const st = e && e.response && e.response.status;
      const detail = e && e.response && e.response.data && e.response.data.detail;
      if (st === 403) return { source: "denied", body: null };
      if (st === 400 && detail && detail.code === "appointment_patient_mismatch") return { source: "mismatch", body: null };
      try {
        const todo = await api.get(`/v1/kg/${practiceId}/todo/${patientId}`);
        const d = todo && todo.data && !todo.patient_insights ? todo.data : todo;
        return { source: "fallback", body: recsFromLegacyTodo(d, { aliases: FALLBACK_ORDER_ALIASES, dos: chartOrderDate }) };
      } catch (e2) {
        const st2 = e2 && e2.response && e2.response.status;
        return {
          source: "fallback",
          body: recsFromLegacyTodo(null, { aliases: FALLBACK_ORDER_ALIASES, dos: chartOrderDate }),
          notRun: st2 === 404,
          failed: st2 !== 404,
        };
      }
    }
  }, [practiceId, patientId, appointmentId, chartOrderDate]);
  const loadCareiq = useCallback(async () => {
    const seqAtGet = ordersSeqRef.current;
    const r = await loadCareiqBody();
    return { ...r, seqAtGet };
  }, [loadCareiqBody]);

  useEffect(() => {
    if (!patientId) return undefined;
    let cancelled = false;
    refetchedForListRef.current = false;
    setSource("loading");
    setBody(null);
    setSessionAdds(new Map());
    setErrors(new Map());
    setViewSnap(null);
    recStackRef.current = [];
    fuStackRef.current = [];
    loadCareiq().then((r) => {
      if (cancelled) return;
      setSource(r.source);
      bodySeqRef.current = r.seqAtGet;
      setBody(r.body);
      setNotRun(Boolean(r.notRun));
      setLoadFailed(Boolean(r.failed));
    });
    return () => { cancelled = true; };
  }, [patientId, loadCareiq]);

  // The documents list cache had expired: ask the GET once more after the
  // tab's own documents call (which refreshes it) returns. Never loops.
  useEffect(() => {
    if (!docsLoaded || source !== "n3" || refetchedForListRef.current) return undefined;
    if (!(body && body.meta && body.meta.documents && body.meta.documents.list_missing)) return undefined;
    refetchedForListRef.current = true;
    let cancelled = false;
    loadCareiq().then((r) => {
      if (cancelled || r.source !== "n3") return;
      bodySeqRef.current = r.seqAtGet;
      setBody((prev) => (prev ? { ...r.body } : r.body));
    });
    return () => { cancelled = true; };
  }, [docsLoaded, source, body, loadCareiq]);

  const fetchOrders = useCallback(async () => {
    if (!patientId) return;
    ordersSeqRef.current += 1;
    const seq = ordersSeqRef.current;
    try {
      const raw = await careiqapi.post(`/v1/${practiceId}/orders/get`, { patientid: patientId, orderdate: chartOrderDate });
      const d = raw && raw.data !== undefined ? raw.data : raw;
      const list = Array.isArray(d) ? d : (d && (d.orders || d.data)) || [];
      setCurrentOrders(list);
      // An order this tab added, or one the GET reported, that is no longer
      // on the visit was deleted (the Current Orders panel here, or the
      // Orders panel): its item must offer Add again, never keep saying
      // "Added" for an order that does not exist.
      if (Array.isArray(list)) {
        const ids = new Set(list.map((o) => (o && o.id != null ? String(o.id) : null)).filter(Boolean));
        const gone = (id) => id != null && !ids.has(String(id));
        setSessionAdds((prev) => {
          let next = null;
          prev.forEach((sa, k) => {
            if (sa.seq < seq && gone(sa.order_id)) {
              if (!next) next = new Map(prev);
              next.delete(k);
            }
          });
          return next || prev;
        });
        if (seq > bodySeqRef.current) setBody((b) => clearGoneOrders(b, gone));
      }
    } catch (e) {
      console.warn("[CareIQ N3] orders fetch failed:", e && e.message);
    } finally {
      setOrdersLoading(false);
    }
  }, [practiceId, patientId, chartOrderDate]);

  const fetchRecordingRows = useCallback(async () => {
    if (!patientId) return;
    try {
      const res = await blinkscribeapi.post(`/v1/patient/${patientId}/recording/details`, {
        filter_array: { date_filter: { start_date: "2020-01-01", end_date: dayjs().format("YYYY-MM-DD") } },
      });
      setBsRows(rowsOf(res));
    } catch (e) {
      console.warn("[CareIQ N3] recording rows fetch failed:", e && e.message);
    }
  }, [patientId]);

  // The patient's lists (labs, documents, encounters, recording rows, progress notes): once per
  // patient, not per visit -- a visit picked on the timeline re-reads only its orders and its CareIQ.
  // All of them start together and each fills the timeline as it arrives (10-13, "Patient timeline is
  // taking a while to load": the encounters used to wait for the document lists, which a stale cache
  // could hold for 15-40 s). The lists that can be slow to rebuild are asked with allow_stale=1, so an
  // old copy shows at once while the server rebuilds it. Every read retries once on 502/503/504 or no
  // answer (a gateway restart).
  const chartDeptId = Number(dataId?.departmentid) || Number(userConfig?.config?.departmentid) || 1;
  useEffect(() => {
    if (!patientId) return undefined;
    let cancelled = false;
    const isCancelled = () => cancelled;
    const read = (fn) => withRetry(fn, { isCancelled });
    const stopRereads = [];
    setTimelineLoading(true);
    setDocsLoaded(false);
    // the previous patient's rows must not linger while this patient's arrive
    setLabPanels([]);
    setDocsByCat(NO_DOCS);
    setListMetas({});
    setEncounters([]);
    setBsRows([]);
    setHandwrittenDocs([]);
    const setMeta = (name) => (m) => { if (!cancelled) setListMetas((prev) => ({ ...prev, [name]: m })); };
    // one list: rows now, and the fresh rows once the server's rebuild lands (re-read)
    const loadList = (name, ask, wrapKey, onList) => read(ask).then((body) => {
      if (cancelled) return;
      const stop = applyListAnswer({
        body, wrapKey, reread: ask, isCancelled, onList: (rows) => { if (!cancelled) onList(rows); }, onMeta: setMeta(name),
      });
      if (stop) stopRereads.push(stop);
    });
    const labs = read(() => careiqapi.get(`/v1/${practiceId}/labs/patient/${patientId}/get`)).then((r) => {
      // the lab LIST only (local DB, v2); no sync on open
      if (cancelled) return;
      const d = r && r.data !== undefined ? r.data : r;
      setLabPanels((d && d.panels) || []);
    }).catch((e) => console.warn("[CareIQ N3] labs fetch failed:", e && e.message));
    const docs = Promise.allSettled([["imagingresult", "imagingresults"], ["clinicaldocument", "clinicaldocuments"]].map(([cat, wrapKey]) =>
      loadList(cat, () => careiqapi.get(`/v1/fhir-mirror/${practiceId}/patients/${patientId}/documents/${cat}`, STALE_OK), wrapKey,
        (rows) => setDocsByCat((prev) => ({ ...prev, [cat]: rows })))
        .catch((e) => console.warn(`[CareIQ N3] ${cat} fetch failed:`, e && e.message)))).then(() => {
      if (!cancelled) setDocsLoaded(true);
    });
    const encs = loadList("encounters",
      () => careiqapi.post(`/v1/fhir-mirror/${practiceId}/patient/${patientId}/encounter`, { departmentid: chartDeptId, limit: 5 }, STALE_OK),
      "encounters", setEncounters)
      .catch((e) => console.warn("[CareIQ N3] encounters fetch failed:", e && e.message));
    const recs = fetchRecordingRows();
    Promise.allSettled([labs, docs, encs, recs]).then(() => { if (!cancelled) setTimelineLoading(false); });
    // handwritten progress notes only flag visits: they never hold the timeline's spinner
    loadList("progressnotes",
      () => careiqapi.post(`/v1/fhir-mirror/${practiceId}/patient/${patientId}/procedures/get`, { category: "ProgressNotes", department_id: 1 }, STALE_OK),
      "encounterdocuments", (rows) => setHandwrittenDocs(handwrittenOnly(rows)))
      .catch((e) => console.warn("[CareIQ N3] progress notes fetch failed:", e && e.message));
    return () => { cancelled = true; stopRereads.forEach((stop) => stop()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, practiceId, chartDeptId]);
  // this visit's orders (the chart's order date)
  useEffect(() => {
    if (!patientId) return;
    setOrdersLoading(true);
    fetchOrders();
  }, [patientId, chartOrderDate, fetchOrders]);

  // Orders changed elsewhere (Orders panel save/delete, Redux orders data).
  useEffect(() => {
    const on = () => fetchOrders();
    eventBus.on("fetchSummary", on);
    return () => eventBus.off("fetchSummary", on);
  }, [fetchOrders]);
  const ordersDataSeen = useRef(false);
  useEffect(() => {
    if (!ordersDataSeen.current) { ordersDataSeen.current = true; return; }
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxOrdersData]);
  // A recording finished somewhere: this visit's rows may have a note now.
  useEffect(() => {
    const on = () => fetchRecordingRows();
    eventBus.on("refreshDashboard", on);
    return () => eventBus.off("refreshDashboard", on);
  }, [fetchRecordingRows]);
  // A chart opened (or the page reloaded) while this visit's note is still
  // being produced is "processing" from its own rows, and the context's
  // completion poll (the recording page's) no longer runs: re-read the rows
  // every 15 s, up to 10 min, so it turns into "Note ready" by itself (the
  // sidebar's LiveScribePanel does the same for the note).
  useEffect(() => {
    if (phase !== "processing" || visitRows.state !== "pending") return undefined;
    let n = 0;
    const t = setInterval(() => {
      n += 1;
      if (n > 40) { clearInterval(t); return; }
      fetchRecordingRows();
    }, 15000);
    return () => clearInterval(t);
  }, [phase, visitRows.state, fetchRecordingRows]);

  useEffect(() => {
    let cancelled = false;
    careiqapi.get(`/v1/${practiceId}/chart-preferences`).then((r) => {
      const b = r && r.data !== undefined ? r.data : r;
      const prefs = b && b.data !== undefined ? b.data : b;
      if (!cancelled && prefs && (prefs.timeline !== undefined || prefs.careiq !== undefined)) setPracticePrefs(prefs);
    }, () => {});
    return () => { cancelled = true; };
  }, [practiceId]);

  useEffect(() => {
    setEncDocuments([]);
    setListMetas((prev) => (prev.encounterdocument ? { ...prev, encounterdocument: null } : prev));
    if (!patientId || !wantsEncDocs) return undefined;
    let cancelled = false;
    let stop = null;
    const isCancelled = () => cancelled;
    const ask = () => careiqapi.get(`/v1/fhir-mirror/${practiceId}/patients/${patientId}/documents/encounterdocument`, STALE_OK);
    withRetry(ask, { isCancelled }).then((body) => {
      if (cancelled) return;
      stop = applyListAnswer({
        body, wrapKey: "encounterdocuments", reread: ask, isCancelled,
        onList: (rows) => { if (!cancelled) setEncDocuments(rows); },
        onMeta: (m) => { if (!cancelled) setListMetas((prev) => ({ ...prev, encounterdocument: m })); },
      });
    }, () => {});
    return () => { cancelled = true; if (stop) stop(); };
  }, [practiceId, patientId, wantsEncDocs]);

  useEffect(() => {
    setApptReason("");
    if (!appointmentId) return undefined;
    let cancelled = false;
    careiqapi.get(`/v1/fhir-mirror/${practiceId}/appointments/${appointmentId}/notes`).then((res) => {
      if (cancelled) return;
      const d = (res && res.data) || res;
      const notes = Array.isArray(d && d.notes) ? d.notes : [];
      setApptReason(notes.map((n) => (n.notetext || "").trim()).filter(Boolean).join(" · "));
    }, () => {});
    return () => { cancelled = true; };
  }, [practiceId, appointmentId]);

  useEffect(() => {
    const onNoteLov = (n) => setNoteLov(Number(n) || 0);
    eventBus.on("noteLovLoaded", onNoteLov);
    return () => eventBus.off("noteLovLoaded", onNoteLov);
  }, []);

  /* ───────── LiveScribe suggestions: persisted per visit ───────── */
  useEffect(() => {
    setPersistedLive(readJson(liveKeyPersist, []) || []);
  }, [liveKeyPersist]);
  useEffect(() => {
    if (!liveKeyPersist || !liveScribeOrders.length) return;
    const list = liveScribeOrders.map((e) => (typeof e === "string" ? { order_name: e } : { order_name: e.order_name || e.order || "", order_type: e.order_type || e.ordertype || null, details: e.details || e.detail || "" }));
    const prev = readJson(liveKeyPersist, []) || [];
    const names = new Set(prev.map((p) => p.order_name));
    const next = [...prev, ...list.filter((p) => p.order_name && !names.has(p.order_name))];
    writeJson(liveKeyPersist, next);
  }, [liveKeyPersist, liveScribeOrders]);
  // A cancelled recording (stopped without saving) leaves no suggestions
  // behind: its orders are dropped and the Suggested DX go back to what they
  // were when it started.
  const prevPhaseRef = useRef(phase);
  const aiDxNowRef = useRef(aiDiagnoses);
  aiDxNowRef.current = aiDiagnoses;
  const dxAtStartRef = useRef(null);
  useEffect(() => {
    const was = prevPhaseRef.current;
    prevPhaseRef.current = phase;
    if (was !== "recording" && phase === "recording") dxAtStartRef.current = aiDxNowRef.current;
    if (was === "recording" && phase === "before") {
      if (liveKeyPersist) {
        try { localStorage.removeItem(liveKeyPersist); } catch (_) { /* non-fatal */ }
      }
      setPersistedLive([]);
      if (v2Key) {
        try { localStorage.removeItem(v2Key); } catch (_) { /* non-fatal */ }
      }
      setV2Stored(null);
      if (reviewKey) {
        try { localStorage.removeItem(reviewKey); } catch (_) { /* non-fatal */ }
      }
      if (dxAtStartRef.current) setAiDiagnoses(dxAtStartRef.current);
    }
    if (phase !== "recording") dxAtStartRef.current = null;
  }, [phase, liveKeyPersist, v2Key, reviewKey]);
  useEffect(() => {
    if (!vp.orderZoneOpen) return undefined;
    // rows present when the zone first shows are not "new" (no slide-in on a reload)
    const t = setTimeout(() => {
      zoneSeenRef.current = true;
      liveOrders.forEach((o) => seenLiveRef.current.add(o.key));
    }, 0);
    return () => clearTimeout(t);
  }, [vp.orderZoneOpen, liveOrders]);

  /* ───────── Suggested DX (copied behaviour of the legacy tab) ───────── */
  const loadedDxKeyRef = useRef(null);
  useEffect(() => {
    if (!persistKey) return;
    const arr = readJson(persistKey, []);
    setConfirmedDx(new Set(Array.isArray(arr) ? arr : []));
    loadedDxKeyRef.current = persistKey;
  }, [persistKey]);
  useEffect(() => {
    if (!persistKey || loadedDxKeyRef.current !== persistKey) return;
    writeJson(persistKey, Array.from(confirmedDx));
  }, [confirmedDx, persistKey]);
  const loadedAiKeyRef = useRef(null);
  useEffect(() => {
    if (!aiDxKey) return;
    const arr = readJson(aiDxKey, []);
    if (Array.isArray(arr) && arr.length) setAiDiagnoses(arr);
    loadedAiKeyRef.current = aiDxKey;
  }, [aiDxKey]);
  useEffect(() => {
    if (!aiDxKey || loadedAiKeyRef.current !== aiDxKey) return;
    writeJson(aiDxKey, aiDiagnoses);
  }, [aiDiagnoses, aiDxKey]);
  useEffect(() => {
    if (!reduxDiagnoses.length) return;
    setConfirmedDx((prev) => {
      const next = new Set(prev);
      reduxDiagnoses.forEach((c) => { if (c.condition_name) next.add(c.condition_name); if (c.icd10) next.add(c.icd10); });
      return next.size === prev.size ? prev : next;
    });
  }, [reduxDiagnoses]);
  useEffect(() => {
    if (!liveScribeDx.length) return;
    setAiDiagnoses((prev) => {
      const seen = new Set(prev.map((d) => (d.description || "").trim()));
      const next = [...prev];
      liveScribeDx.forEach((entry) => {
        if (typeof entry !== "string") return;
        const m = entry.match(/^([A-Z][0-9A-Z.]+)\s*-\s*(.+)$/);
        const obj = m ? { icd10_code: m[1].trim(), description: m[2].trim() } : { icd10_code: "", description: entry.trim() };
        if (!seen.has(obj.description)) { seen.add(obj.description); next.push(obj); }
      });
      return next.length === prev.length ? prev : next;
    });
  }, [liveScribeDx]);
  useEffect(() => {
    // Only this visit's recording (the event carries no patient or visit).
    const onDx = (d) => {
      if (!liveBelongsRef.current || !d) return;
      setAiDiagnoses((prev) => (prev.some((x) => x.description === d.description) ? prev : [...prev, { icd10_code: d.icd10_code, description: d.description }]));
    };
    eventBus.on("liveScribeDiagnosis", onDx);
    return () => eventBus.off("liveScribeDiagnosis", onDx);
  }, []);

  const handleConfirmDx = useCallback(async (dx) => {
    if (!ordersOn) return;
    const key = dx.description || dx.icd10_code || "";
    if (confirmedDx.has(key)) return;
    setConfirmedDx((prev) => new Set([...prev, key]));
    try {
      await careiqapi.post(`/v1/${practiceId}/patient/${patientId}/orders/confirm`, {
        patient_name: `${dataId?.firstname || ""} ${dataId?.lastname || ""}`.trim(),
        createdby: me || "care-ui",
        orderdate: chartOrderDate, tov: "", lov: 0,
        diagnosis: [dx.icd10_code].filter(Boolean),
        appointmentid: dataId?.appointmentid, encounterid: chartEncounterId, token_no: 0,
        confirm_status: "C", update_status: "C",
      });
      eventBus.emit("fetchSummary");
    } catch (err) {
      console.error("[CareIQ N3] Failed to confirm Dx:", err && err.message);
    }
  }, [ordersOn, confirmedDx, practiceId, patientId, dataId, chartOrderDate, chartEncounterId, me]);

  /* ───────── state POST (CONTRACT §6) ───────── */
  const postState = useCallback(async (changes, scope, { record = true } = {}) => {
    if (!interactive) return false;                       // a picked visit is view only (owner 10-09)
    if (!changes.length || !stateAvailable(bodyRef.current) || source !== "n3") return false;
    if (isPreviewBody(bodyRef.current)) return false;     // specialty preview: view only (utils/careiqPreview.js)
    const snapshot = bodyRef.current;
    const optimistic = optimisticUpdated(changes, { by: me, appointmentid: visitApptId });
    setBody((b) => applyStateUpdates(b, optimistic));
    if (record && changes.some((c) => c.status === "reviewed")) {
      const group = changes.filter((c) => c.status === "reviewed").map((c) => c.key);
      if (scope === "fu") fuStackRef.current = [...fuStackRef.current, group];
      else recStackRef.current = [...recStackRef.current, group];
    }
    setSaveError(null);
    try {
      const res = await careiqapi.post(`/v1/kg/${practiceId}/careiq/${patientId}/state`, { changes });
      if (!res || !Array.isArray(res.updated)) throw new Error("unexpected state answer");
      setBody((b) => applyStateUpdates(b, res.updated));
      return true;
    } catch (e) {
      const st = e && e.response && e.response.status;
      // revert exactly the keys this request touched
      const keys = new Set();
      changes.forEach((c) => { keys.add(c.key); (c.cascade || []).forEach((k) => keys.add(k.key)); });
      setBody((b) => {
        if (!b || !snapshot) return b;
        const prevRec = new Map(recs(snapshot).map((r) => [r.issue_key, r]));
        const prevRow = new Map(fuRows(snapshot).map((r) => [r.key, r]));
        let next = {
          ...b,
          recommendations: recs(b).map((r) => (keys.has(r.issue_key) && prevRec.get(r.issue_key) ? { ...r, status: prevRec.get(r.issue_key).status, reviewed: prevRec.get(r.issue_key).reviewed } : r)),
          followup: { ...b.followup, rows: fuRows(b).map((r) => (keys.has(r.key) && prevRow.get(r.key) ? { ...r, status: prevRow.get(r.key).status, reviewed: prevRow.get(r.key).reviewed } : r)) },
        };
        if (st === 503) next = { ...next, meta: { ...next.meta, state: "unavailable", state_reason: "table_missing" } };
        return next;
      });
      if (st !== 503) setSaveError({ changes, scope });
      return false;
    }
  }, [interactive, source, me, visitApptId, practiceId, patientId]);

  /* ───────── orders: Add / Undo (CONTRACT §7) ───────── */
  // the ONE orders/create body (N3/orderCreate.js; the live Orders card and the review place with the same)
  const createBody = useCallback((ordername, ordertype, diagnosis = [], opts = {}) => orderCreateBody({
    patientName: `${dataId?.firstname || ""} ${dataId?.lastname || ""}`.trim(),
    me,
    orderDate: chartOrderDate,
    appointmentId,
    fallbackAppointmentId: dataId?.appointmentid,
    encounterId: chartEncounterId,
    provider: chartProviderName,
  }, ordername, ordertype, diagnosis, opts), [dataId, me, chartOrderDate, appointmentId, chartEncounterId, chartProviderName]);

  const setBusy = useCallback((key, on) => setBusyKeys((prev) => { const n = new Set(prev); if (on) n.add(key); else n.delete(key); return n; }), []);
  const setErr = useCallback((key, msg) => setErrors((prev) => { const n = new Map(prev); if (msg) n.set(key, msg); else n.delete(key); return n; }), []);

  // kind: "rec" (drawer / band) | "fu" (a Follow up row) | "live" (a LiveScribe suggestion)
  const handleAdd = useCallback(async (item, kind) => {
    if (!ordersOn) return;                                // no CareIQ orders in this practice (7825)
    const a = kind === "live" ? null : item.action;
    // A LiveScribe suggestion that IS an item's order (same key) is filed
    // under that action's name and type (the practice's catalogue name, D9),
    // whether or not the item is still open (a reviewed potassium row still
    // names its repeat "BMP", never LiveScribe's "Basic Metabolic Panel (BMP)").
    const twin = kind === "live"
      ? [...recs(bodyRef.current), ...fuRows(bodyRef.current)].map((x) => x.action).find((x) => x && x.order_key === item.key && x.ordername)
      : null;
    const ordername = kind === "live" ? (twin ? twin.ordername : item.name) : a.ordername;
    const ordertype = kind === "live"
      ? (twin && twin.ordertype) || mapOrderType(item.order_type, item.name)
      : (a.ordertype || mapOrderType(null, a.ordername));
    const key = kind === "live" ? item.key : a.order_key || orderKey(a.ordername, aliases);
    if (!key || busyKeys.has(key) || isAdded(key) || sessionAdds.has(key)) return;
    if (isPreviewBody(bodyRef.current)) {                 // specialty preview: view only, no order is created
      setErr(key, "Preview · not added");
      return;
    }
    const pre = before;
    setBusy(key, true);
    setErr(key, null);
    setJustDone(kind === "rec" ? item.issue_key : null);
    let res = null;
    try {
      const diagnosis = kind === "rec" ? orderDxDiagnosis(item, dxPick.get(item.issue_key)) : [];
      // the end-of-visit review's Today / Schedule toggle (owner 10-24; placeWhen is set by the review only - the
      // drawer / band / v2 zone adds send no schedule flag, as before)
      const opts = kind === "live" && (item.placeWhen === "today" || item.placeWhen === "schedule") ? { when: item.placeWhen } : {};
      res = await careiqapi.post(`/v1/${practiceId}/patient/${patientId}/orders/create`, createBody(ordername, ordertype, diagnosis, opts));
    } catch (e) {
      res = null;
    }
    setBusy(key, false);
    // Failure can come back as HTTP 201 {"status":201,"message":"Unable To Create Order"}.
    if (!(res && res.status === 200 && Number.isInteger(res.data))) {
      setErr(key, "Not added · Retry");
      return false;
    }
    const orderId = res.data;
    // the reason under the Current Orders row: a served headline as one plain line ("finding · action",
    // never its separator arrow; recText.js reasonText), any other reason as written
    const why = kind === "live" ? (item.details || "") : reasonText(a.why, item.headline);
    setSessionAdds((prev) => new Map(prev).set(key, { order_id: orderId, ordertype, ordername, from: kind, pre, why, itemKey: item.issue_key || item.key || null, seq: ordersSeqRef.current }));
    setCurrentOrders((prev) => [...prev, { id: orderId, ordername, ordertype, status: "CREATED", _added: true, _why: why }]);
    setFreshOrderId(orderId);
    eventBus.emit("refreshDashboard");
    fetchOrders();
    // The state row remembers the order was added here (Undo survives a reload).
    const ev = { type: "added", order_id: orderId };
    const b = bodyRef.current;
    if (kind === "rec") {
      // one order covers every row that shares it (careiq.n3.4 order_shared_with: the server offers an order
      // once, the other rows point to it): they are handled with it
      postState([item, ...sharersOf(openRecs(b), item, key)].map((r) => buildChange(r, "reviewed", { appointmentid: visitApptId, orderEvent: ev, cascade: false })), "rec");
    } else if (kind === "fu") {
      postState([buildChange(item, item.status, { appointmentid: visitApptId, orderEvent: ev, cascade: false })], "fu", { record: false });
    } else {
      // A LiveScribe suggestion with the same key as an open item: that item
      // is done too (the same thing is never done twice).
      const rec = openRecs(b).find((r) => r.action && r.action.order_key === key);
      const row = openFuRows(b).find((r) => r.action && r.action.order_key === key);
      const changes = [];
      // ...and so is every row that shares that item's order (careiq.n3.4 order_shared_with), as with the
      // drawer's own add: one order handles them all (adversarial review 10-07: they stayed open for good)
      if (rec) [rec, ...sharersOf(openRecs(b), rec, key)].forEach((r) => changes.push(buildChange(r, "reviewed", { appointmentid: visitApptId, orderEvent: ev, cascade: false })));
      if (row) changes.push(buildChange(row, row.status, { appointmentid: visitApptId, orderEvent: ev, cascade: false }));
      if (changes.length) postState(changes, "rec", { record: Boolean(rec) });
    }
    return true;
  }, [ordersOn, aliases, busyKeys, isAdded, sessionAdds, before, practiceId, patientId, createBody, fetchOrders, postState, visitApptId, setBusy, setErr, dxPick]);

  /* ───────── order suggestions v2: the provider's actions (adds go through handleAdd, nothing else orders) ───────── */
  // Monitoring only (BlinkScribe logs concept ids): what was added, un-ticked, re-picked.
  const v2Feedback = useCallback((event, concepts, prechecked = []) => {
    if (!v2Payload || !apptKey || !concepts.length) return;
    Promise.resolve(blinkscribeapi.post("/v1/recording/order-suggestions/feedback", {
      appointmentid: Number(apptKey), patientid: Number(patientId), event, concepts, prechecked,
    })).catch(() => {});
  }, [v2Payload, apptKey, patientId]);
  const v2Add = useCallback(async (row) => {
    const ok = await handleAdd(row, "live");
    if (ok) v2Feedback("added", [row.concept], row.prechecked ? [row.concept] : []);
  }, [handleAdd, v2Feedback]);
  const v2AddChecked = useCallback(async (rows) => {
    const done = [];
    for (const r of rows) {
      // one order at a time, through the same add as the row's ⊕
      // eslint-disable-next-line no-await-in-loop
      if (await handleAdd(r, "live")) done.push(r);
    }
    if (done.length) v2Feedback("bulk_added", done.map((r) => r.concept), done.filter((r) => r.prechecked).map((r) => r.concept));
  }, [handleAdd, v2Feedback]);
  const v2Toggle = useCallback((row, on) => {
    setV2Checks((prev) => new Map(prev).set(row.concept, on));
    if (row.prechecked && !on) v2Feedback("unchecked", [row.concept], [row.concept]);
    else if (!row.prechecked && on) v2Feedback("checked", [row.concept]);
  }, [v2Feedback]);
  const v2Pick = useCallback((row, name) => {
    setV2Picks((prev) => new Map(prev).set(row.concept, name));
    v2Feedback("variant", [row.concept]);
  }, [v2Feedback]);

  /* ───────── end-of-visit order review (owner 10-20; components/OrderReview, enabled providers only) ───────── */
  // BlinkScribe's payload says whether this provider has the review screen (review.enabled). After the stop
  // it opens once by itself (final payload, or the last live one when the final pass is late); the Suggested
  // card's "Review orders" reopens it; sign-off reopens it while not confirmed (signOffNote). It places the
  // ticked rows only on Confirm, each through handleAdd (the ⊕ path), and re-reads the visit's orders first.
  const reviewOn = Boolean(ordersOn && v2Payload && reviewEnabled(v2Payload));
  const reviewVisitKey = visitKeyOf(patientId, apptKey);
  const reviewCtxRef = useRef(null);
  reviewCtxRef.current = reviewOn ? {
    payload: v2Payload,
    aliases,
    place: async (row) => (await handleAdd({ key: row.key, name: row.name, order_type: row.order_type, details: row.reason || "", placeWhen: row.when }, "live")) === true,
    fetchOrders: async () => {
      const raw = await careiqapi.post(`/v1/${practiceId}/orders/get`, { patientid: patientId, orderdate: chartOrderDate });
      const d = raw && raw.data !== undefined ? raw.data : raw;
      return Array.isArray(d) ? d : (d && (d.orders || d.data)) || [];
    },
    // Today / Schedule of an "Already ordered" row (owner 10-24): the dashboard's existing call, scoped to this visit
    setWhen: async (orderId, when) => {
      const r = await changeVisitOrderWhen({
        practiceId, me, patientId, orderDate: chartOrderDate,
        fetchOrders: async () => {
          const raw = await careiqapi.post(`/v1/${practiceId}/orders/get`, { patientid: patientId, orderdate: chartOrderDate });
          const d = raw && raw.data !== undefined ? raw.data : raw;
          return Array.isArray(d) ? d : (d && (d.orders || d.data)) || null;
        },
      }, orderId, when);
      if (r.ok) { eventBus.emit("refreshDashboard"); fetchOrders(); }
      return r;
    },
    whenEditable: placedWhenEditable(chartOrderDate),
  } : null;
  useEffect(() => {
    if (reviewOn && reviewVisitKey) registerOrderReviewVisit(reviewVisitKey, reviewCtxRef);
  }, [reviewOn, reviewVisitKey]);
  const afterStop = phase === "processing" || phase === "noteReady";
  const [reviewLate, setReviewLate] = useState(false);
  useEffect(() => {
    setReviewLate(false);
    if (!reviewOn || !afterStop || v2Final) return undefined;
    const t = setTimeout(() => setReviewLate(true), REVIEW_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [reviewOn, afterStop, v2Final, reviewVisitKey]);
  useEffect(() => {
    if (!reviewOn || !afterStop || !reviewKey || !(v2Final || reviewLate) || liveCardOn) return;
    const rec = readRecord(reviewKey) || emptyRecord();
    if (rec.state === "confirmed" || rec.autoShown) return;
    writeRecord(reviewKey, { ...rec, autoShown: true });
    openOrderReview({ patientId, apptKey, trigger: "stop" });
  }, [reviewOn, afterStop, v2Final, reviewLate, reviewKey, patientId, apptKey, liveCardOn]);
  const openReview = useCallback(() => openOrderReview({ patientId, apptKey, trigger: "chart" }), [patientId, apptKey]);

  const v2Zone = v2Payload ? {
    rows: v2RowList, discussed: v2Discussed(v2Payload), placed: v2Placed(v2Payload), final: Boolean(v2Payload.final),
    other: v2OtherRows, onAddOther: (o) => handleAdd(o, "live"),
    onToggle: v2Toggle, onPick: v2Pick, onAddChecked: v2AddChecked,
    onReview: reviewOn && afterStop ? openReview : null,
  } : null;

  // Undo an order THIS tab created (session, or existing_order.added_here):
  // the patient-scoped delete, then the state row.
  const handleUndoAdd = useCallback(async (item, kind) => {
    if (!ordersOn) return;
    const a = item.action;
    const key = a.order_key || orderKey(a.ordername, aliases);
    const s = sessionAdds.get(key);
    const ex = a.existing_order;
    const orderId = s ? s.order_id : ex && ex.added_here ? ex.order_id : null;
    if (!orderId || busyKeys.has(key)) return;
    const ordertype = (s && s.ordertype) || (ex && ex.ordertype) || a.ordertype;
    setBusy(key, true);
    setErr(key, null);
    let status = 0;
    try {
      const r = await careiqapi.delete(`/v1/${practiceId}/orders/delete/multiple`, {
        data: { ordersArray: [orderId], orderdate: chartOrderDate, patientid: Number(patientId), ordertype },
      });
      status = r && Number(r.status) ? Number(r.status) : 200;
    } catch (e) {
      status = (e && e.response && e.response.status) || 0;
    }
    setBusy(key, false);
    // 404: not this patient's, or already gone; the delete is all-or-nothing.
    if (status !== 200 && status !== 404) {
      setErr(key, "Not removed · Retry");
      return;
    }
    setSessionAdds((prev) => { const n = new Map(prev); n.delete(key); return n; });
    setBody((b) => setExistingOrder(b, key, null));
    setCurrentOrders((prev) => prev.filter((o) => o.id !== orderId));
    eventBus.emit("refreshDashboard");
    fetchOrders();
    const ev = { type: "undone", order_id: orderId };
    if (kind === "rec") {
      const handled = sharersOf(recs(bodyRef.current), item, key).filter((r) => r.status === "reviewed" && r.reviewed && r.reviewed.via === "order");
      postState([item, ...handled].map((r) => buildChange(r, "open", { appointmentid: visitApptId, orderEvent: ev, cascade: false })), "rec", { record: false });
    }
    else postState([buildChange(item, item.status === "reviewed" ? "reviewed" : "open", { appointmentid: visitApptId, orderEvent: ev, cascade: false })], "fu", { record: false });
  }, [ordersOn, aliases, sessionAdds, busyKeys, practiceId, chartOrderDate, patientId, fetchOrders, postState, visitApptId, setBusy, setErr]);

  /* ───────── undo (a rec is handled by adding its order: owner 10-07, no review control) ───────── */
  const undoRec = useCallback((rec) => {
    if (!rec || rec.status === "open") return;
    // a row that shares another row's order is undone with that row (the order is placed once)
    const host = rec.order_shared_with ? recs(bodyRef.current).find((r) => r.issue_key === rec.order_shared_with) : null;
    if (host && host.status === "reviewed" && host.reviewed && host.reviewed.via === "order") {
      undoRec(host);
      return;
    }
    const ex = rec.action && rec.action.existing_order;
    const key = rec.action && (rec.action.order_key || orderKey(rec.action.ordername, aliases));
    if (rec.reviewed && rec.reviewed.via === "order" && key && (sessionAdds.has(key) || (ex && ex.added_here))) {
      handleUndoAdd(rec, "rec");
      return;
    }
    postState([buildChange(rec, "open", { appointmentid: visitApptId })], "rec", { record: false });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aliases, sessionAdds, handleUndoAdd, postState, visitApptId]);
  const undoLastRec = useCallback(() => {
    const b = bodyRef.current;
    const byKey = new Map(recs(b).map((r) => [r.issue_key, r]));
    let stack = recStackRef.current.map((g) => g.filter((k) => byKey.get(k) && byKey.get(k).status === "reviewed")).filter((g) => g.length);
    let group = stack.pop();
    recStackRef.current = stack;
    if (!group) {
      const newest = newestReviewed(reviewedThisVisit(b));
      group = newest ? [newest.issue_key] : [];
    }
    // (a row that shares the order of another row in the same group is undone with it)
    const inGroup = new Set(group);
    group.filter((k) => !(byKey.get(k) && byKey.get(k).order_shared_with && inGroup.has(byKey.get(k).order_shared_with)))
      .forEach((k) => undoRec(byKey.get(k)));
  }, [undoRec]);

  const reviewRow = useCallback((row) => {
    postState([buildChange(row, "reviewed", { appointmentid: visitApptId, textSnapshot: row.short })], "fu");
  }, [postState, visitApptId]);
  const undoRow = useCallback((row) => {
    if (!row || row.status === "open") return;
    postState([buildChange(row, "open", { appointmentid: visitApptId })], "fu", { record: false });
  }, [postState, visitApptId]);
  const markAllRows = useCallback((rows) => {
    const changes = rows.filter((r) => r.status === "open").map((r) => buildChange(r, "reviewed", { appointmentid: visitApptId }));
    if (changes.length) postState(changes, "fu");
    setFuShow(false);
  }, [postState, visitApptId]);
  const undoLastRow = useCallback(() => {
    const b = bodyRef.current;
    const rowByKey = new Map(fuRows(b).map((r) => [r.key, r]));
    const stack = fuStackRef.current.map((g) => g.filter((k) => rowByKey.get(k) && rowByKey.get(k).status === "reviewed")).filter((g) => g.length);
    let group = stack.pop();
    fuStackRef.current = stack;
    if (!group) {
      const newest = newestReviewed(reviewedFuThisVisit(b));
      group = newest ? [newest.key] : [];
    }
    const changes = group.map((k) => rowByKey.get(k)).filter(Boolean).map((r) => buildChange(r, "open", { appointmentid: visitApptId }));
    if (changes.length) postState(changes, "fu", { record: false });
  }, [postState, visitApptId]);

  /* ───────── order edit / delete (copied panel; patient-scoped delete) ───────── */
  const handleEditOrder = useCallback((order) => {
    setEditingOrder(order);
    setEditingName(order.ordername || order.description || order.name || "");
    setConfirmDeleteOrder(null);
    setEditError("");
  }, []);
  const handleSaveOrderName = useCallback(async () => {
    if (!editingOrder || !editingName.trim()) return;
    setEditingSaving(true);
    try {
      await careiqapi.put(`/v1/${practiceId}/orders/${patientId}/update-visit-order/${editingOrder.id}`, { ordername: editingName.trim() });
      setCurrentOrders((prev) => prev.map((o) => (o.id === editingOrder.id ? { ...o, ordername: editingName.trim() } : o)));
      setEditingOrder(null);
      setEditingName("");
      await fetchOrders();
      eventBus.emit("fetchSummary");
    } catch (err) {
      console.error("[CareIQ N3] Failed to update order:", err && err.message);
    } finally {
      setEditingSaving(false);
    }
  }, [editingOrder, editingName, practiceId, patientId, fetchOrders]);
  const handleDeleteOrder = useCallback(async (order) => {
    setEditingSaving(true);
    setEditError("");
    try {
      // Patient-scoped: deletes nothing unless the id is this patient's, in
      // the table the CHART's order date picks (never delete/order/{id}).
      await careiqapi.delete(`/v1/${practiceId}/orders/delete/multiple`, {
        data: { ordersArray: [order.id], orderdate: chartOrderDate, patientid: Number(patientId), ordertype: order.ordertype },
      });
      setCurrentOrders((prev) => prev.filter((o) => o.id !== order.id));
      setEditingOrder(null);
      setEditingName("");
      setConfirmDeleteOrder(null);
      await fetchOrders();
      eventBus.emit("fetchSummary");
    } catch (err) {
      const st = err && err.response && err.response.status;
      setEditError(st === 404 ? "Not deleted: this order is not on this patient's visit." : "Not deleted · try again");
      console.error("[CareIQ N3] Failed to delete order:", err && err.message);
    } finally {
      setEditingSaving(false);
    }
  }, [practiceId, chartOrderDate, patientId, fetchOrders]);

  /* ───────── lab sync: lazy (search 2+ chars, Show all) ───────── */
  // The sync is a paid Athena call: at most ONE per chart open, even when the
  // list read after it fails (each later keystroke only re-reads the list).
  const labSyncedRef = useRef(null);
  const loadAllLabs = useCallback(async () => {
    if (allLabsLoaded || allLabsLoading || !patientId) return;
    setAllLabsLoading(true);
    try {
      // (never where the practice keeps it off: careiqLabSync(), 7825)
      if (labSyncedRef.current !== patientId && careiqLabSync()) {
        labSyncedRef.current = patientId;
        try { await careiqapi.get(`/v1/${practiceId}/patient/${patientId}/labresults`); } catch (_) { /* best-effort */ }
      }
      const resp = await careiqapi.get(`/v1/${practiceId}/labs/patient/${patientId}/get`);
      const d = resp && resp.data !== undefined ? resp.data : resp;
      setLabPanels((d && d.panels) || []);
      setAllLabsLoaded(true);
    } catch (err) {
      console.warn("[CareIQ N3] full labs fetch failed:", err);
    } finally {
      setAllLabsLoading(false);
    }
  }, [allLabsLoaded, allLabsLoading, patientId, practiceId]);

  const saveTimelinePrefs = useCallback(async () => {
    if (!userConfig?.id) { setTlPrefsOpen(false); return; }
    setTlPrefsSaving(true);
    try {
      await api.put(`/v1/${practiceId}/user/config`, { rowid: userConfig.id, timeline_docs: tlDraft });
      dispatch(setConfig({ ...userConfig, config: { ...userConfig.config, timeline_docs: tlDraft } }));
      setTlPrefsOpen(false);
    } catch (e) {
      console.error("[CareIQ N3] failed to save timeline preferences:", e);
    } finally {
      setTlPrefsSaving(false);
    }
  }, [userConfig, tlDraft, dispatch, practiceId]);

  /* ───────── timeline events (the legacy tab's rules) ───────── */
  // The lab list by result day: the timeline's lab events and the lab popup a
  // record link opens are built from the same grouping.
  const labDays = useMemo(() => labsByDay(labPanels), [labPanels]);
  const docLabels = useMemo(() => {
    const m = {};
    ((body && body.document_labels) || []).forEach((dl) => { if (dl && dl.documentid && dl.label) m[dl.documentid] = dl.label; });
    return m;
  }, [body]);
  const timelineEvents = useMemo(() => {
    const events = [];
    const HIDDEN = new Set(["CANCELLED", "CANCELED", "ENTERED-IN-ERROR", "NO-SHOW", "NOSHOW", "NO SHOW", "X"]);
    const REAL_VISIT_RE = /OFFICE\s*\/?\s*OUTPATIENT VISIT|OFFICE VISIT|FOLLOW\s*UP|NEW PT|NEW PATIENT|WALK[\s-]*IN/i;
    const ANCILLARY_RE = /\bINJ\b|INJECT|IMMUNIZ|VACCIN|SWAB|BLOOD TEST|GLUCOSE|VITAMIN|\bASSAY\b|THER\/PROPH|VENIPUNCTURE/i;
    const thisYear = dayjs().year();
    encounters.forEach((enc) => {
      const status = String(enc.status || enc.appointmentstatus || "").trim().toUpperCase();
      if (HIDDEN.has(status) || enc.has_clinical_content === false) return;
      const encText = `${enc.encountertype || ""} ${enc.encountervisitname || ""} ${enc.appointmenttypename || ""}`;
      const looksAncillary = !REAL_VISIT_RE.test(encText) && ANCILLARY_RE.test(encText);
      const d = dayjs(enc.startdate || enc.date || enc.encounterdate);
      if (!d.isValid()) return;
      const encId = String(enc.encounterid || "");
      const hasHandwritten = handwrittenDocs.some((hw) => {
        if (encId && String(hw.encounterid || "") === encId) return true;
        const hwDt = dayjs(hw.createddatetime || hw.createddate);
        if (!hwDt.isValid()) return false;
        const diff = hwDt.diff(d, "day");
        return diff >= 0 && diff <= 7;
      });
      if (looksAncillary && !hasHandwritten && enc.has_clinical_content !== true) return;
      const prov = `${enc.providerfirstname || ""} ${enc.providerlastname || ""}`.trim();
      events.push({ date: d, dateStr: d.format("MMM D"), yearBadge: d.year() !== thisYear ? String(d.year()) : null, label: prov || enc.appointmenttypename || "Office Visit", type: "visit", dotColor: "normal", hasHandwritten, raw: enc });
    });
    const encDates = new Set(events.map((e) => e.date.format("YYYY-MM-DD")));
    const bsSeen = new Set();
    bsRows.filter((r) => r && (r.transcriptionstatus === "Summarized" || r.transcriptionstatus === "APPROVED")).forEach((rec) => {
      const d = dayjs(rec.ts || rec.created_at || rec.date);
      if (!d.isValid()) return;
      const key = d.format("YYYY-MM-DD");
      if (encDates.has(key) || bsSeen.has(key)) return;
      bsSeen.add(key);
      const prov = String(rec.recorded_by || rec.providername || "").trim();
      events.push({ date: d, dateStr: d.format("MMM D"), yearBadge: null, label: prov || "Office Visit (BlinkScribe note)", type: "visit", dotColor: "normal", hasHandwritten: false, raw: { bsOnly: true, startdate: key, encounterid: "", appointmenttypename: "Office Visit (BlinkScribe note)" } });
    });
    Object.values(labDays).forEach((group) => {
      const names = [...group.panels];
      const hasAbnormal = group.tests.some((t) => isOutOfRange(t.latestResult && t.latestResult.value, t.refrange || (t.latestResult && t.latestResult.refrange)));
      const searchText = group.tests.map((t) => `${t.testname || t.name || ""} ${t.panelname || ""} ${(t.latestResult && t.latestResult.value) || ""} ${(t.latestResult && (t.latestResult.units || t.latestResult.unit)) || ""}`).join(" | ");
      events.push({ date: group.date, dateStr: group.date.format("MMM D"), yearBadge: null, label: names.length ? names.join("/") : "Labs", type: "lab", dotColor: hasAbnormal ? "abnormal" : "normal", searchText, raw: { dateStr: group.date.format("MMM D, YYYY"), tests: group.tests } });
    });
    const shownImagingIds = timelineDocs.includes("imagingresult") ? imagingIdSet(documents.filter((x) => x && x.imagingresultid)) : new Set();
    const docEntries = [];
    [...documents, ...encDocuments].forEach((doc) => {
      const d = dayjs(doc.createddate || doc.createddatetime);
      if (!d.isValid() || isImagingReportTwin(doc, shownImagingIds)) return;
      const docId = doc.imagingresultid || doc.encounterdocumentid || doc.clinicaldocumentid;
      const kgLabel = docId ? docLabels[docId] : null;
      const cat = docCategory(doc);
      if (!timelineDocs.includes(cat)) return;
      const labelText = kgLabel || doc.label || doc.documentdescription || doc.description || (cat === "clinicaldocument" ? "Document" : cat === "encounterdocument" ? "Note" : "Imaging");
      if (!wantsEncDocs && /^\s*progress\s*note\s*$/i.test(labelText)) return;
      docEntries.push({ doc, cat, id: docIdOf(doc), d, day: d.format("YYYY-MM-DD"), labelText });
    });
    // One row per multi-take upload (D2): Follow up's groups (doc.members), else the same 120 s
    // same-title rule here; the row opens the newest file
    groupTakes(docEntries, serverGroups(fuRows(body))).forEach((g) => {
      const e = g.newest;
      const n = g.entries.length;
      events.push({
        date: e.d, dateStr: e.d.format("MMM D"), yearBadge: null, label: n > 1 ? groupLabel(g, (x) => x.labelText) : e.labelText,
        type: e.cat === "imagingresult" ? "imaging" : "document", dotColor: "pending", raw: { ...e.doc, patientid: patientId },
        ...(n > 1 ? { members: g.members, searchText: g.entries.map((x) => x.labelText).join(" | ") } : {}),
      });
    });
    events.sort((a, b) => b.date.valueOf() - a.date.valueOf());
    return events;
  }, [encounters, bsRows, labDays, documents, encDocuments, docLabels, patientId, handwrittenDocs, timelineDocs, wantsEncDocs, body]);

  // An event Follow up already shows (labs by date, documents by id; after the
  // last visit, or filed ON its day: Follow up's window starts there) is not
  // listed twice. Only while Follow up offers it
  // as a click: an open row (shown, or behind "N more · Show") or a folded
  // normal result. A row reviewed (it moves to "Reviewed today", which opens
  // nothing) or a Follow up the user's sections hide gives the record back
  // to the timeline, so it can always be opened with one click.
  // A group (D2): while its row is offered, EVERY take is hidden (the timeline
  // lists the group as one row, so hiding it hides them all); once it is not,
  // the timeline's one "Electrocardiogram ×3" row opens the newest.
  const coveredByFollowUp = useCallback((ev) => {
    if (noVisit || !showFollowUp || source !== "n3") return false;
    const rows = fuRows(body).filter((r) => (r.display === "row" && r.status === "open") || r.display === "folded");
    const day = ev.date.format("YYYY-MM-DD");
    if (ev.type === "lab") return rows.some((r) => r.kind === "fu_lab" && r.date === day);
    if (ev.type === "imaging" || ev.type === "document") {
      // by any file of the event (a FHIR-only document by its resource id) against any file of the row
      const ids = ev.members || [docIdOf(ev.raw)].filter(Boolean);
      return ids.length > 0 && rows.some((r) => {
        if (!r.doc) return false;
        const covers = new Set([...(Array.isArray(r.doc.members) ? r.doc.members.map(String) : []), String(r.doc.documentid != null ? r.doc.documentid : r.doc.resource_id)]);
        return ids.some((id) => covers.has(String(id)));
      });
    }
    return false;
  }, [body, showFollowUp, source, noVisit]);

  const handleTimelineClick = useCallback((ev) => {
    if (!ev.raw) return;
    // a visit opens its note (the visit popup), as always; on a chart with no visit today the tab
    // also moves to that visit (its date, its CareIQ, view only) until "Back to today"
    if (ev.type === "visit") {
      setVisitModal(ev.raw);
      // (the picked visit is also the sidebar mic's recording target; refused while a recording runs)
      if (!chartLive) setPicked(visitFromTimeline(ev, patientId));
    }
    else if (ev.type === "lab") setLabModal(ev.raw);
    else if (ev.type === "imaging" || ev.type === "document") { setDocModal(ev.raw); setDocModalOpen(true); }
    else setModalItem(ev.raw);
  }, [chartLive, patientId, setPicked]);

  // A document by its {documentid, resource_id, category} triple. A FHIR
  // outside report has no numeric id: its list id comes as resource_id, and
  // it opens by that id (the viewer fetches nothing without an id field). The
  // list row is matched in the record's own category first.
  const openReport = useCallback((row) => {
    const doc = row.doc || {};
    const docId = doc.documentid || doc.resource_id || null;
    const id = String(docId || "");
    const field = { imagingresult: "imagingresultid", clinicaldocument: "clinicaldocumentid", encounterdocument: "encounterdocumentid" }[doc.category];
    const all = [...documents, ...encDocuments];
    const found = !id ? null
      : field ? all.find((d) => String(d[field] || "") === id) || null
        : all.find((d) => String(d.imagingresultid || d.clinicaldocumentid || d.encounterdocumentid || "") === id) || null;
    const ids = doc.category === "clinicaldocument"
      ? { clinicaldocumentid: docId, documentclass: "CLINICALDOCUMENT" }
      : doc.category === "encounterdocument"
        ? { encounterdocumentid: docId, documentclass: "ENCOUNTERDOCUMENT" }
        : { imagingresultid: docId };
    const raw = found ? { ...found, patientid: patientId } : { patientid: patientId, fhir_resource_id: doc.resource_id || null, ...ids };
    setDocModal(raw);
    setDocModalOpen(true);
  }, [documents, encDocuments, patientId]);

  // A record link (a recommendation's evidence, a Follow up lab row; records
  // as sourceLinks.openRecord / rowRecord make them): a lab opens the lab
  // popup of that result date from the loaded list with the record's tests
  // marked (the record's own key values when the list has nothing on that
  // day); a document opens the document viewer (Follow up's path).
  const openRecord = useCallback((r) => {
    if (!r) return;
    if (r.kind === "lab") setLabModal(labGroupFor(labDays, r) || labGroupFromRecord(r));
    else if (r.kind === "doc") openReport({ doc: r.ref });
  }, [labDays, openReport]);

  /* ───────── layout effects: sizes, sticky band, overlay ───────── */
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setRootH(el.clientHeight));
    ro.observe(el);
    setRootH(el.clientHeight);
    return () => ro.disconnect();
  }, [source]);
  useEffect(() => {
    const w = bandWrapRef.current;
    const host = wrapRef.current;
    if (!w || !host || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => host.style.setProperty("--n3-band-h", `${w.offsetHeight}px`));
    ro.observe(w);
    return () => ro.disconnect();
  }, [source]);
  // the sticky band casts a shadow once content scrolls under it
  useEffect(() => {
    const w = bandWrapRef.current;
    if (!w) return undefined;
    const scroller = desktop ? rootRef.current : scrollerOf(w);
    if (!scroller) return undefined;
    const on = () => {
      const sticky = w.classList.contains("sticky") && getComputedStyle(w).position === "sticky";
      const top = scroller.getBoundingClientRect ? scroller.getBoundingClientRect().top : 0;
      const stuck = sticky && (desktop ? scroller.scrollTop > 4 : w.getBoundingClientRect().top <= top + 1 && scroller.scrollTop > 4);
      w.classList.toggle("is-stuck", stuck);
    };
    scroller.addEventListener("scroll", on, { passive: true });
    on();
    return () => scroller.removeEventListener("scroll", on);
  }, [desktop, source, bandMode]);

  // Follow up collapsing to its lead must never leave the critical row
  // scrolled out of the timeline column.
  const prevFuMode = useRef(fuModeValue);
  useEffect(() => {
    const was = prevFuMode.current;
    prevFuMode.current = fuModeValue;
    if (was === "full" && fuModeValue === "lead" && !stacked && tlScrollRef.current && tlScrollRef.current.scrollTop > 0 && tlScrollRef.current.scrollTo) {
      tlScrollRef.current.scrollTo({ top: 0, behavior: reducedMotion() ? "auto" : "smooth" });
    }
  }, [fuModeValue, stacked]);

  // leaving the drawer (recording starts / escape) or entering it resets the list snapshot
  const prevBandMode = useRef(bandMode);
  useEffect(() => {
    if (prevBandMode.current !== bandMode) {
      prevBandMode.current = bandMode;
      if (!ovlOpen) setViewSnap(null);
    }
  }, [bandMode, ovlOpen]);
  useEffect(() => {
    if (bandMode === "drawer" && viewSnap === null && body) setViewSnap(openRecs(body).map((r) => r.issue_key));
  }, [bandMode, viewSnap, body]);

  const sizeOvl = useCallback(() => {
    const w = bandWrapRef.current;
    if (!w) return;
    const top = w.getBoundingClientRect().top;
    const bottom = desktop && rootRef.current ? rootRef.current.getBoundingClientRect().bottom : window.innerHeight;
    setOvlMax(Math.max(260, Math.floor(bottom - top - 8)));
  }, [desktop]);

  const closeOvl = useCallback((refocus) => {
    setOvlOpen(false);
    setViewSnap(null);
    if (refocus) {
      setTimeout(() => {
        const b = bandWrapRef.current && bandWrapRef.current.querySelector('[data-act="showall"]');
        if (b) b.focus({ preventScroll: true });
      }, 0);
    }
  }, []);
  const openOvl = useCallback(() => {
    const b = bodyRef.current;
    const open = openRecs(b);
    setViewSnap(open.map((r) => r.issue_key));
    if (!open.length) setRevOpen(true);
    if (!desktop) {
      // Stacked: the overlay hangs from the band, below Follow up; keep the
      // critical row in view above it when both fit.
      const w = bandWrapRef.current;
      const scroller = scrollerOf(w);
      if (w && scroller) {
        const sTop = scroller === document.scrollingElement ? 0 : scroller.getBoundingClientRect().top;
        const sH = scroller === document.scrollingElement ? window.innerHeight : scroller.clientHeight;
        const r = w.getBoundingClientRect();
        let dy = r.top - sTop < TOP_CLEAR || r.top - sTop > sH * 0.35 ? r.top - sTop - TOP_CLEAR : 0;
        const sm = sinceMainRef.current;
        const crit = sm && sm.querySelector(".n3-fu-row.crit");
        if (crit && sm.offsetHeight + TOP_CLEAR + 8 <= sH * 0.5) dy = sm.getBoundingClientRect().top - sTop - TOP_CLEAR;
        if (Math.abs(dy) > 2 && scroller.scrollBy) scroller.scrollBy(0, dy);
      }
    }
    setOvlAnim(!reducedMotion());
    setOvlOpen(true);
  }, [desktop]);
  useLayoutEffect(() => { if (ovlOpen) sizeOvl(); }, [ovlOpen, sizeOvl]);
  // A viewer opened from the overlay (a record link) sits above it: its
  // clicks and its Esc belong to the viewer, never close the overlay.
  const viewerOpenRef = useRef(false);
  viewerOpenRef.current = Boolean(labModal) || Boolean(docModalOpen && docModal) || Boolean(visitModal) || Boolean(modalItem);
  useEffect(() => {
    if (!ovlOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !viewerOpenRef.current) { e.preventDefault(); closeOvl(true); } };
    const onDown = (e) => {
      if (viewerOpenRef.current) return;
      const t = e.target;
      if (t.closest && (t.closest("#n3-ovl") || t.closest('[data-act="showall"]'))) return;
      closeOvl(false);
    };
    const onScroll = () => sizeOvl();
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("resize", onScroll);
    const scroller = desktop ? rootRef.current : scrollerOf(bandWrapRef.current);
    if (scroller) scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", onScroll);
      if (scroller) scroller.removeEventListener("scroll", onScroll);
    };
  }, [ovlOpen, closeOvl, sizeOvl, desktop]);
  // the overlay belongs to band / line mode only
  useEffect(() => { if (bandMode === "drawer" && ovlOpen) closeOvl(false); }, [bandMode, ovlOpen, closeOvl]);

  const flash = useCallback((key) => {
    setFlashKey(key);
    setTimeout(() => setFlashKey((k) => (k === key ? null : k)), 1500);
  }, []);
  const goBand = useCallback((issueKey) => {
    if (bandMode === "drawer") {
      flash(issueKey);
      const row = bandWrapRef.current && bandWrapRef.current.querySelector(`[data-rec="${issueKey}"]`);
      if (row && row.scrollIntoView) row.scrollIntoView({ block: "nearest", behavior: reducedMotion() ? "auto" : "smooth" });
    } else {
      openOvl();
      flash(issueKey);
    }
  }, [bandMode, flash, openOvl]);
  // a shared order's pointer: to the row that adds it, its order button focused once it is on screen
  const goToRow = useCallback((issueKey) => {
    goBand(issueKey);
    setTimeout(() => {
      const root = wrapRef.current;
      const b = root && root.querySelector(`[data-rec="${issueKey}"] [data-act="add"], [data-done="${issueKey}"] [data-act="undo-add"]`);
      if (b) b.focus({ preventScroll: true });
    }, 60);
  }, [goBand]);
  const fuBelow = useCallback(() => {
    const t = tlScrollRef.current;
    if (!t) return;
    const sc = scrollerOf(t);
    const top = sc && sc.getBoundingClientRect && sc !== document.scrollingElement ? sc.getBoundingClientRect().top : 0;
    if (sc && sc.scrollBy) sc.scrollBy({ top: t.getBoundingClientRect().top - top - TOP_CLEAR, behavior: reducedMotion() ? "auto" : "smooth" });
  }, []);
  const escapeOpen = useCallback(() => {
    vp.openEscape();
    // Stacked: bring the order zone into view when it lands below the fold.
    setTimeout(() => {
      if (desktop) return;
      const z = wrapRef.current && wrapRef.current.querySelector(".n3-ozone");
      if (!z) return;
      const r = z.getBoundingClientRect();
      if (r.top < window.innerHeight - 140) return;
      const scroller = scrollerOf(z);
      if (scroller && scroller.scrollBy) scroller.scrollBy({ top: r.top - (scroller.getBoundingClientRect ? scroller.getBoundingClientRect().top : 0) - TOP_CLEAR, behavior: reducedMotion() ? "auto" : "smooth" });
    }, 260);
  }, [vp, desktop]);
  const escapeClose = useCallback(() => {
    vp.closeEscape();
    setTimeout(() => { if (escapeRef.current) escapeRef.current.focus({ preventScroll: true }); }, 260);
  }, [vp]);

  /* ───────── view context for the components ───────── */
  const confirmText = useCallback((rec) => {
    if (!rec.reviewed || rec.reviewed.via !== "order") return "Marked reviewed";
    const key = rec.action && (rec.action.order_key || orderKey(rec.action.ordername, aliases));
    const s = key && sessionAdds.get(key);
    const ex = rec.action && rec.action.existing_order;
    if (s) return addedText({ from: s.from, pre: s.pre, addedHere: true });
    return addedText({ from: "rec", pre: true, addedHere: ex ? Boolean(ex.added_here) : true });
  }, [aliases, sessionAdds]);
  const noteFor = useCallback((rec) => {
    const at = clockTime(rec.reviewed && rec.reviewed.at);
    const by = rec.reviewed && rec.reviewed.by && rec.reviewed.by !== me ? ` by ${rec.reviewed.by}` : "";
    // a row that shares another row's order (careiq.n3.4 order_shared_with) was handled by that order
    const host = !rec.action && rec.order_shared_with ? recsByKey.get(rec.order_shared_with) : null;
    const act = rec.action || (host && host.action);
    if (rec.reviewed && rec.reviewed.via === "order" && act) return `${act.ordername} added to this visit’s orders${by} · ${at}`;
    return `${by ? `Reviewed${by}` : "Marked reviewed"} · ${at}`;
  }, [me, recsByKey]);
  const stateNote = source === "fallback"
    ? (loadFailed ? null : "CareIQ is showing its older list · reviewed state unavailable")
    : body && meta && meta.state === "unavailable" ? "Reviewed state is not saved for this practice yet" : null;
  const emptyText = source === "fallback"
    ? (notRun ? "CareIQ has not run for this patient" : loadFailed ? "CareIQ could not be loaded · close and reopen the chart to try again" : "Nothing open")
    : meta && meta.todo_file === "missing" ? "CareIQ has not run for this patient"
      : meta && meta.todo_file === "unreadable" ? "CareIQ results could not be read"
        : allRecs.length ? `Nothing open — all ${totalShown} reviewed today.` : "Nothing open";
  const recCtx = {
    stateOk, flashKey, justDone, infoFor, confirmText, noteFor, emptyText, stateNote,
    refYear: dosYear,
    critLabs: criticalLabs(body),
    onOpenEvidence: openRecord,
    onAdd: (rec) => handleAdd(rec, "rec"),
    onUndoAdd: (rec) => handleUndoAdd(rec, "rec"),
    dxPickFor: (rec) => dxPick.get(rec.issue_key) || null,
    onPickDx: (rec, code) => setDxPick((prev) => new Map(prev).set(rec.issue_key, code)),
    onUndo: undoRec,
    // a shared order's pointer: the row that adds it (flashed, scrolled to, its order focused)
    onGoTo: goToRow,
    // a next step's link: the chart tab where the work happens
    onNext: (ns) => { const tab = ns && NEXT_TAB[ns.target]; if (tab != null && chartTabAllowed(tab) && onSwitchTab) onSwitchTab(tab); },
    allOrdered,
    onUndoLast: undoLastRec,
    revOpen,
    onToggleRev: () => setRevOpen((v) => !v),
    revItems: (view) => reviewedThisVisit(body).filter((r) => !view.includes(r.issue_key)),
  };
  const fuCtx = {
    stateOk, me, before, stacked, fuShow, fuOpen, fuMore, flashKey, recsByKey, foldedOpen,
    infoFor: (row) => {
      const i = infoFor(row);
      // a Follow up row's order confirms where it came from
      if (i && i.state === "added" && row.action) {
        const s = sessionAdds.get(row.action.order_key);
        if (s && s.from === "live") return { ...i, text: "Added from Suggested Orders" };
      }
      return i;
    },
    onAdd: (row) => handleAdd(row, "fu"),
    onUndoAdd: (row) => handleUndoAdd(row, "fu"),
    onReview: reviewRow,
    onUndoRow: undoRow,
    onUndoLast: undoLastRow,
    onMarkAll: markAllRows,
    revOpen: fuRevOpen,
    onToggleRev: () => setFuRevOpen((v) => !v),
    onToggleFolded: () => setFoldedOpen((v) => !v),
    onShow: () => { setFuShow(true); setFuRevOpen(true); },
    onHide: () => setFuShow(false),
    onOpen: () => setFuOpen(true),
    onMore: () => setFuMore(true),
    onLess: () => setFuMore(false),
    onClose: () => {
      setFuOpen(false);
      if (stacked && sinceMainRef.current) {
        const sc = scrollerOf(sinceMainRef.current);
        const top = sinceMainRef.current.getBoundingClientRect().top - (sc && sc.getBoundingClientRect ? sc.getBoundingClientRect().top : 0);
        if (sc && sc.scrollBy && top < TOP_CLEAR) sc.scrollBy(0, top - TOP_CLEAR);
      }
    },
    onBelow: fuBelow,
    onGoBand: goBand,
    onOpenReport: openReport,
    refYear: dosYear,
    onOpenRecord: openRecord,
  };

  const fuBlock = (part) => (body && !noVisit && showFollowUp && source === "n3" ? <FollowUp body={body} mode={fuModeValue} part={part} ctx={fuCtx} /> : null);
  // desktop / iPad landscape: the timeline column fills the CareIQ root (sticky), so its card
  // reaches the bottom of the screen like the old timeline column, whatever Follow up holds
  const sidebarStyle = desktop && !stacked && rootH ? { height: `${rootH}px`, maxHeight: `${rootH}px` } : undefined;
  // (none where the practice places no CareIQ orders: 7825)
  const zoneVisible = vp.orderZoneOpen && ordersOn;
  const [zoneAppear, setZoneAppear] = useState(false);
  const prevZone = useRef(zoneVisible);
  useEffect(() => {
    if (zoneVisible && !prevZone.current && !reducedMotion()) {
      setZoneAppear(true);
      const t = setTimeout(() => setZoneAppear(false), 260);
      prevZone.current = zoneVisible;
      return () => clearTimeout(t);
    }
    prevZone.current = zoneVisible;
    return undefined;
  }, [zoneVisible]);

  /* ───────── render ───────── */
  const loadingBody = source === "loading";
  // the no-visit screen: today's card that did not happen ("Today's 10:30 AM visit is marked no show"),
  // and the chart card's own past / future visit, offered as one click (never opened by itself)
  const todayNotHeld = cardInfo && cardInfo.kind === "today" && cardInfo.statusWord
    ? `Today's ${cardInfo.time ? `${cardInfo.time} ` : ""}visit is marked ${cardInfo.statusWord.toLowerCase()}.`
    : "";
  // Today's card that did not happen is offered too (the patient may arrive late): selecting it lets the
  // provider record on it, after a confirmation that warns it is marked cancelled / no show (10-09).
  const cardOffer = cardInfo && (cardInfo.kind === "past" || cardInfo.kind === "future") && !visitNotHeld(dataId) && cardInfo.dayKey
    ? { label: `Open the ${dayjs(cardInfo.dayKey).format("MMM D, YYYY")} visit`, onOpen: () => setPicked({ ...dataId, picked: "card" }) }
    : cardInfo && cardInfo.kind === "today" && (cardInfo.statusWord === "No show" || cardInfo.statusWord === "Cancelled") && cardInfo.appointmentid
      ? { label: `Select today's ${cardInfo.time ? `${cardInfo.time} ` : ""}visit (${cardInfo.statusWord.toLowerCase()})`, onOpen: () => setPicked({ ...dataId, picked: "card" }) }
      : null;
  return (
    <OrdersShownContext.Provider value={interactive}>
    <div className="ciq3 n3-wrap" ref={wrapRef} data-testid="careiq-n3" data-phase={phase} data-visit={chartLive ? "today" : noVisit ? "none" : "picked"}>
      <DosRow
        dateText={dosText}
        provider={chartProviderName}
        phase={phase}
        visitText={visitText}
        elapsed={vp.elapsed}
        paused={vp.paused}
        noVisit={noVisit}
        statusWord={visitStatus}
      />
      {!chartLive && visit ? (
        <div className="n3-viewing" role="status" data-testid="n3-viewing">
          <span>Viewing visit of <b>{dayjs(chartOrderDate).format("MMM D, YYYY")}</b></span>
          {visitStatus ? <span className="n3-viewing-st">{visitStatus}</span> : null}
          <span className="n3-sep" aria-hidden="true">·</span>
          <span className="n3-viewing-ro">view only</span>
          {/* the recording target (utils/recordingTarget.js): what the sidebar mic records on */}
          {recTarget.status === "resolving" ? <span className="n3-viewing-rec" data-testid="n3-viewing-rec">· finding its appointment…</span> : null}
          {recTarget.status === "unresolved" ? <span className="n3-viewing-rec" data-testid="n3-viewing-rec">· no appointment to record on</span> : null}
          {/* (not while this patient's recording runs: its visit is where the note goes) */}
          {lockHere ? null : (
            <button type="button" className="n3-lnk no-hover-tint" data-act="back-to-today" onClick={() => setPicked(null)}>Back to today</button>
          )}
        </div>
      ) : null}
      {lockHere && targetState.notice ? (
        <div className="n3-notice" role="status" data-testid="n3-recording-locked">
          {targetState.notice}{" "}
          <button type="button" className="n3-lnk no-hover-tint" onClick={dismissRecordingNotice}>OK</button>
        </div>
      ) : null}
      {previewLine(meta) ? (
        <div className="n3-notice n3-preview-line" role="note" data-testid="careiq-preview-line">{previewLine(meta)}</div>
      ) : null}
      {saveError ? (
        <div className="n3-notice" role="status">
          Not saved ·{" "}
          <button type="button" className="n3-lnk no-hover-tint" onClick={() => { const s = saveError; setSaveError(null); postState(s.changes, s.scope, { record: false }); }}>Retry</button>
        </div>
      ) : null}
      <div className="n3-root" ref={rootRef}>
        <div className={`n3-layout${stacked ? " is-stacked" : ""}`} data-layout={stacked ? "stacked" : "side"}>
          <div className="n3-main">
            {noVisit ? (
              <NoVisitToday statusLine={todayNotHeld} offer={cardOffer} />
            ) : loadingBody ? (
              <div className="n3-loading"><span className="n3-spin" />Loading CareIQ…</div>
            ) : source === "denied" ? (
              <div className="n3-notice">CareIQ is not available for this sign-in</div>
            ) : source === "mismatch" ? (
              <div className="n3-notice">Visit does not match this chart</div>
            ) : (
              <>
                {stacked && showFollowUp && source === "n3" ? (
                  <section className="n3-since-main" ref={sinceMainRef} aria-label="Follow up since last visit" data-testid="n3-since-main">
                    {fuBlock(split ? "lead" : undefined)}
                  </section>
                ) : null}
                {showBand ? (
                  <div className={`n3-band-wrap${bandMode !== "drawer" ? " sticky" : ""}`} ref={bandWrapRef}>
                    <RecsDrawer
                      mode={bandMode}
                      view={viewKeys}
                      byKey={recsByKey}
                      open={openList}
                      total={totalShown}
                      ctx={recCtx}
                      merged={merged}
                      ovlOpen={ovlOpen}
                      onShowAll={() => (ovlOpen ? closeOvl(true) : openOvl())}
                      onEscape={ordersOn ? escapeOpen : undefined}
                      escapeRef={escapeRef}
                    />
                    {ovlOpen ? (
                      <RecsOverlay
                        view={viewKeys}
                        byKey={recsByKey}
                        total={totalShown}
                        ctx={recCtx}
                        merged={merged}
                        maxHeight={ovlMax}
                        animate={ovlAnim}
                        onClose={() => closeOvl(true)}
                      />
                    ) : null}
                  </div>
                ) : null}
                {zoneVisible ? (
                  <OrderZone
                    phase={phase}
                    appear={zoneAppear}
                    liveDx={showLive ? liveDx : []}
                    liveOrders={showLive && !liveCardOn ? liveOrderRows : []}
                    showSuggested={showSuggested}
                    currentDx={currentDx}
                    orders={filteredOrders}
                    ordersLoading={ordersLoading}
                    infoForOrder={infoForOrder}
                    freshOrderId={freshOrderId}
                    lovCode={visitLovCode}
                    apptReason={apptReason}
                    onConfirmDx={handleConfirmDx}
                    onAddLive={showLive && v2Zone ? v2Add : (o) => handleAdd(o, "live")}
                    v2={showLive && !liveCardOn ? v2Zone : null}
                    onDetail={(o) => setModalItem({ label: o.name, value: o.details || "" })}
                    onHideOrders={escapeClose}
                    edit={{
                      editing: editingOrder, name: editingName, setName: setEditingName, saving: editingSaving,
                      confirmDelete: confirmDeleteOrder, setConfirmDelete: setConfirmDeleteOrder, error: editError,
                      onEdit: handleEditOrder, onSave: handleSaveOrderName, onDelete: handleDeleteOrder,
                      onCancel: () => { setEditingOrder(null); setEditingName(""); setEditError(""); },
                    }}
                  />
                ) : null}
              </>
            )}
          </div>
          <PatientTimeline
            sidebarRef={sidebarRef}
            sidebarStyle={sidebarStyle}
            scrollRef={tlScrollRef}
            events={timelineEvents}
            search={timelineSearch}
            onSearch={(v) => { setTimelineSearch(v); if (v.trim().length >= 2) loadAllLabs(); }}
            loading={timelineLoading || allLabsLoading}
            asOfLine={documentsAsOfLine(listMetas, "Timeline")}
            followUp={stacked ? (restShown ? fuBlock("rest") : null) : fuBlock(undefined)}
            lastVisitDate={lastVisitDate}
            dosYear={dosYear}
            dosKey={noVisit ? null : (meta && meta.visit && meta.visit.dos) || chartOrderDate}
            coveredByFollowUp={coveredByFollowUp}
            expanded={timelineExpanded}
            onToggleExpand={() => { const next = !timelineExpanded; setTimelineExpanded(next); if (next) loadAllLabs(); }}
            onEventClick={handleTimelineClick}
            prefs={{
              open: tlPrefsOpen, draft: tlDraft, setDraft: setTlDraft, saving: tlPrefsSaving,
              onOpen: () => { setTlDraft(timelineDocs); setTlPrefsOpen(true); },
              onClose: () => setTlPrefsOpen(false),
              onSave: saveTimelinePrefs,
            }}
          />
        </div>
      </div>

      {modalItem && <DetailModal item={modalItem} onClose={() => setModalItem(null)} />}
      {visitModal && (
        <VisitSummaryModal
          encounter={visitModal}
          patientId={patientId}
          patientInfo={{ name: `${dataId?.firstname || ""} ${dataId?.lastname || ""}`.trim(), id: patientId, dob: dataId?.dob || "" }}
          onClose={() => setVisitModal(null)}
        />
      )}
      {labModal && (
        <LabsMiniModal
          labGroup={labModal}
          onClose={() => setLabModal(null)}
          // no "Open Full Labs" where the chart has no Labs tab (7825)
          onSwitchToLabs={chartTabAllowed(1) ? () => { setLabModal(null); if (onSwitchTab) onSwitchTab(1); } : undefined}
        />
      )}
      {docModalOpen && docModal && (
        <ProcedureDocument isProcedureDocumentOpen={docModalOpen} setIsProcedureDocumentOpen={setDocModalOpen} procedure={docModal} subview={1} />
      )}
    </div>
    </OrdersShownContext.Provider>
  );
}

